import React, { useState, useEffect, useCallback } from 'react';
import { OWASP_OPTIONS } from '../constants/owasp';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiErrorMessage } from '../utils/apiError';
import { Card, Button, Badge, Input, Select, TextArea, Modal, Table } from '../components/Common';
import CvssCalculator from '../components/CvssCalculator';
import ScanManager from '../components/ScanManager';
import StepsToReproduce, { parseSteps } from '../components/StepsToReproduce';
import RemediationTimeline from '../components/RemediationTimeline';
import AiSuggestPanel from '../components/AiSuggestPanel';
import AgentScanLaunchModal from '../components/AgentScanLaunchModal';

import TemplatePicker from '../components/TemplatePicker';
// Tab ids, so `?tab=reports` can open the page straight on that tab. Kept
// module-level because the TABS array below is built from loaded data and
// isn't available when the initial tab is chosen.
const TAB_IDS = ['overview', 'findings', 'scope', 'reports', 'documents', 'notes', 'activity'];

// What the Reports tab accepts, for the picker's accept attribute and for the
// drop zone's own check — a drop ignores accept, so both read this list.
const REPORT_UPLOAD_EXTENSIONS = ['.pdf', '.docx', '.doc', '.xlsx', '.pptx'];

const STATUSES = ['Requested', 'New', 'Scoping', 'Contract Sent', 'In Progress', 'In Review', 'Complete',
                  'In Retest', 'Retest Complete', 'On Hold', 'Cancelled'];
const SEVERITIES = ['Critical', 'High', 'Medium', 'Low', 'Info'];
const SCOPE_TYPES = ['URL', 'IP Address', 'API Endpoint', 'CIDR Range', 'Domain', 'Mobile App'];
// Mirrors NETWORK_SCOPE_TYPES in backend/services/engagement-types.js: the
// types that scope themselves from the Networks inventory.
const NETWORK_SCOPE_TYPES = ['Network Penetration Test', 'Network Segmentation'];
const usesNetworkScope = (t) => NETWORK_SCOPE_TYPES.includes(t);
const ENGAGEMENT_TYPES = ['Network Penetration Test', 'Network Segmentation', 'Application Penetration Test', 'Mobile Application Test', 'Red Team', 'API Penetration Test'];

// Types that test one identifiable application. A network test or a red
// team exercise covers an estate, so neither is prompted for a link.
// Kept in step with backend/services/engagement-types.js.
const APPLICATION_TYPES = ['Application Penetration Test', 'Mobile Application Test', 'API Penetration Test'];
const ENV_TYPES = ['Production', 'DEV', 'Test', 'UAE', 'Other'];

// Whether the testing was credentialed. Kept identical to VALID_APPROACHES in
// backend/routes/engagements.js and to the CHECK constraint in
// add-engagement-testing-approach.sql, because this string is printed verbatim
// into a customer's report as %TESTINGAPPROACH%.
//
// There is no blank option. An engagement is always one or the other, and an
// empty approach on a report reads as an omission rather than an answer.
const APPROACHES = ['Black Box - Unauthenticated', 'Gray Box - Authenticated'];
const DEFAULT_APPROACH = 'Gray Box - Authenticated';

const FILE_CATEGORIES = ['General', 'Scope Document', 'Rules of Engagement', 'NDA', 'Authorization', 'Network Diagram', 'API Collection', 'Other'];
const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = { monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday', thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday' };
const TIME_ZONES = [
  { value: '', label: 'Select Time Zone' },
  { value: 'US/Eastern', label: 'US/Eastern (ET)' },
  { value: 'US/Central', label: 'US/Central (CT)' },
  { value: 'US/Mountain', label: 'US/Mountain (MT)' },
  { value: 'US/Pacific', label: 'US/Pacific (PT)' },
  { value: 'US/Alaska', label: 'US/Alaska (AKT)' },
  { value: 'US/Hawaii', label: 'US/Hawaii (HT)' },
  { value: 'UTC', label: 'UTC' },
];
const DEFAULT_RESTRICTIONS = { noRestriction: true, timeZone: '', monday: { start: '', end: '' }, tuesday: { start: '', end: '' }, wednesday: { start: '', end: '' }, thursday: { start: '', end: '' }, friday: { start: '', end: '' }, saturday: { start: '', end: '' }, sunday: { start: '', end: '' } };

export default function EngagementDetail() {
  const { id } = useParams();
  const { apiFetch, user, hasGroup, isReadOnlyViewer } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [eng, setEng] = useState(null);
  const [testers, setTesters] = useState([]);
  const [vulnTemplates, setVulnTemplates] = useState([]);
  // Bumped on every template fill, to remount the steps editor.
  const [templateFillKey, setTemplateFillKey] = useState(0);
  const [reportTemplates, setReportTemplates] = useState([]);
  const [launchAgent, setLaunchAgent] = useState(false);
  const [showFindingModal, setShowFindingModal] = useState(false);
  const [showScopeModal, setShowScopeModal] = useState(false);
  // Picking scope out of the client's Networks inventory.
  const [showNetworkPicker, setShowNetworkPicker] = useState(false);
  const [networkOptions, setNetworkOptions] = useState([]);
  const [selectedNetworkIds, setSelectedNetworkIds] = useState([]);
  const [newNetwork, setNewNetwork] = useState({ name: '', value: '', purpose: '', pci: 'false' });
  const [networkError, setNetworkError] = useState('');
  const [networkBusy, setNetworkBusy] = useState(false);
  const [pickerFilter, setPickerFilter] = useState({ search: '', tag: '', pciOnly: false });
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [editFinding, setEditFinding] = useState(null);
  // The finding opened for reading. Separate from editFinding so opening a row
  // cannot put the record into an editable state by accident.
  const [viewFinding, setViewFinding] = useState(null);
  const [findingForm, setFindingForm] = useState({});
  const [scopeForm, setScopeForm] = useState({});
  const [files, setFiles] = useState([]);
  const [uploadCategory, setUploadCategory] = useState('General');
  const [uploading, setUploading] = useState(false);
  const [reportUploadCategory, setReportUploadCategory] = useState('Final Report');
  const [uploadingReport, setUploadingReport] = useState(false);
  const [evidence, setEvidence] = useState([]);
  const [evidenceUploading, setEvidenceUploading] = useState(false);
  const [editingCaption, setEditingCaption] = useState(null);
  const [playbookItems, setPlaybookItems] = useState([]);
  const [playbookSummary, setPlaybookSummary] = useState(null);
  const [playbookLoading, setPlaybookLoading] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [editingNotes, setEditingNotes] = useState(null);
  const [showEditEngModal, setShowEditEngModal] = useState(false);
  const [engEditForm, setEngEditForm] = useState({});
  const [editSows, setEditSows] = useState([]);
  const [editVendors, setEditVendors] = useState([]);
  const [burpImporting, setBurpImporting] = useState(false);
  const [showBurpModal, setShowBurpModal] = useState(false);
  const [burpFile, setBurpFile] = useState(null);
  const [burpResult, setBurpResult] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportName, setReportName] = useState('');
  const [suggestedReportName, setSuggestedReportName] = useState('');
  const [generatedReports, setGeneratedReports] = useState([]);
  const [showDeliveryModal, setShowDeliveryModal] = useState(null);
  const [passwordModal, setPasswordModal] = useState(null);   // the report row
  const [passwordData, setPasswordData] = useState(null);     // { userPassword, ownerPassword?, access }
  const [passwordShares, setPasswordShares] = useState(null); // { shares, candidates }
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [shareUserId, setShareUserId] = useState('');
  const [protectingReport, setProtectingReport] = useState(null);
  // Whether this deployment can convert and encrypt PDFs at all. Both run
  // through Gotenberg, which is a docker-compose service locally but is not in
  // the ECS task definition -- so on a deployment without it the button would
  // appear and answer 503. Unknown until the fetch lands; the button waits.
  const [gotenbergEnabled, setGotenbergEnabled] = useState(false);
  const [editingReportVersion, setEditingReportVersion] = useState(null);
  const [editReportVersionVal, setEditReportVersionVal] = useState('');
  const [deliveryForm, setDeliveryForm] = useState({ deliveryMethod: 'email', recipientName: '', recipientEmail: '', notes: '' });
  const [showRevokeModal, setShowRevokeModal] = useState(null);
  const [showRemediateForm, setShowRemediateForm] = useState(false);
  const [remediateNotes, setRemediateNotes] = useState('');
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [activityLog, setActivityLog] = useState([]);
  const [revokeReason, setRevokeReason] = useState('');
  const [reportDeliveries, setReportDeliveries] = useState({});
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [generating, setGenerating] = useState(false);
  // What the user ticked, and what this deployment can produce at all.
  const [selectedFormats, setSelectedFormats] = useState(['docx']);
  const [reportFormats, setReportFormats] = useState(['docx']);
  const [reportVersion, setReportVersion] = useState('');
  // Certificates are an MSP-issued attestation, so only MSP accounts are offered
  // the choice -- and it starts off. A certificate records a verifiable claim
  // about a document, which is a deliberate act, while most generations are
  // drafts; defaulting it on certified work-in-progress by omission.
  const [includeCertificate, setIncludeCertificate] = useState(false);
  const [clientIntegrations, setClientIntegrations] = useState([]);
  const [findingTickets, setFindingTickets] = useState([]);
  const [syncing, setSyncing] = useState(null);
  const [bulkSyncing, setBulkSyncing] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [credentials, setCredentials] = useState([]);
  const [showCredentialModal, setShowCredentialModal] = useState(false);
  const [newCredential, setNewCredential] = useState({ label: '', credential_type: 'bearer_token', username: '', secret: '', extra_config: {} });
  const [engNotes, setEngNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [aiAnalyzing, setAiAnalyzing] = useState(null);
  const [aiResult, setAiResult] = useState(null);
  const [aiTitle, setAiTitle] = useState('');
  const [showAiModal, setShowAiModal] = useState(false);

  // Linking an existing engagement to an application. The list is only fetched
  // when the modal opens, since most engagements already have one.
  const [showLinkAppModal, setShowLinkAppModal] = useState(false);
  const [linkableApps, setLinkableApps] = useState([]);
  const [linkAppId, setLinkAppId] = useState('');
  const [linkingApp, setLinkingApp] = useState(false);
  const [linkAppError, setLinkAppError] = useState('');
  // An unrecognised ?tab= falls back to findings rather than rendering nothing.
  const [activeTab, setActiveTab] = useState(() => {
    const requested = searchParams.get('tab');
    return TAB_IDS.includes(requested) ? requested : 'findings';
  });
  const [selectedFindingId, setSelectedFindingId] = useState(null);
  // Depth, not a boolean: dragging across a child fires dragleave on the parent,
  // and a boolean would flicker the highlight off mid-drag.
  const [reportDragDepth, setReportDragDepth] = useState(0);
  const [reportUploadError, setReportUploadError] = useState('');

  const isAdmin = hasGroup('Engagement Admins');
  const isTester = user?.id === eng?.tester_id;
  const isClientTester = user?.clientId && user?.clientId === eng?.client_id &&
    hasGroup('Penetration Testers');
  const isClientAdmin = user?.groups?.includes('Client Admins') && !user?.groups?.includes('Super Admins');
  const canEdit = isAdmin || isTester || isClientTester || isClientAdmin;
  // Credentials are tester tooling and hold live secrets, so they are limited
  // to the people doing the testing and the admins responsible for the
  // engagement — a narrower set than canEdit, which also admits Client Admins.
  // hasGroup already answers true for Super Admins.
  const canEditCredentials = isAdmin || isTester || isClientTester ||
    hasGroup('Penetration Testers') || hasGroup('MSP Penetration Testers');
  const canAssignTester = isAdmin || isClientAdmin;
  // The same people who can pick an application when creating an engagement:
  // the application lookup is limited to them, so nobody else could load the
  // list to choose from.
  const canLinkApplication = isAdmin || isClientAdmin || hasGroup('Application Managers');
  // Only types that test one application, and only while none is linked.
  const needsApplication = !!eng && !eng.application_id &&
    APPLICATION_TYPES.includes(eng.engagement_type);
  // Report generation is gated on Report Generators alone — editing an
  // engagement no longer implies being able to produce its report.
  // hasGroup already returns true for Super Admins, the one exception.
  const canReport = hasGroup('Report Generators');

  // 'MSP Penetration Testers' is provider-side staff; 'Penetration Testers'
  // without the prefix is the CLIENT-side group -- different people entirely.
  // hasGroup() cannot express this because it answers true for Super Admins on
  // every group name, which would be right for the Super Admin branch and
  // wrong for the is_msp one, so the array is read directly. This must agree
  // with requireMspPenTester in backend/routes/reports.js or the button shows
  // and then 403s.
  const isMspPenTester = (user?.groups || []).includes('Super Admins')
    || (!!user?.isMsp && (user?.groups || []).includes('MSP Penetration Testers'));

  // A report may be generated once testing has actually started and the
  // engagement carries the dates the document reports on. It used to require
  // Complete or In Review, which made the button vanish for the whole period
  // the agent is filing findings — the point at which you most want to read
  // them. On Hold and Cancelled are excluded deliberately: neither is further
  // along than In Progress, and a report from a cancelled engagement would
  // state a testing window that did not happen.
  // Mirrors REPORTABLE_STATUSES in backend/routes/reports.js. The retest
  // statuses are reportable because the revalidation report is produced
  // from them.
  const REPORTABLE_STATUSES = ['In Progress', 'In Review', 'Complete', 'In Retest', 'Retest Complete'];
  const reportBlockedReason = (() => {
    if (!eng) return 'Loading engagement';
    if (!REPORTABLE_STATUSES.includes(eng.status)) {
      return `Engagement must be In Progress or later to generate a report (currently ${eng.status})`;
    }
    const missing = [!eng.start_date && 'start date', !eng.end_date && 'end date'].filter(Boolean);
    if (missing.length) return `Set the engagement ${missing.join(' and ')} before generating a report`;
    return '';
  })();
  const canGenerateReport = !reportBlockedReason;
  const canEditBilling = hasGroup('MSP Billing Admins') || hasGroup('Client Billing Admins') || hasGroup('Super Admins');

  const [billingSows, setBillingSows] = useState([]);
  const [billingSaving, setBillingSaving] = useState(false);
  const [billingForm, setBillingForm] = useState({ sowId: '', sowCredits: 1 });
  const [billingDirty, setBillingDirty] = useState(false);

  const orchFetch = useCallback(async (path, options = {}) => {
    const token = sessionStorage.getItem('token');
    const headers = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    };
    return fetch(`/orchestrator-api/api${path}`, { ...options, headers });
  }, []);

  const analyzeWithAi = useCallback(async (findingId, title) => {
    setAiAnalyzing(findingId);
    setAiResult(null);
    setAiTitle(title || 'Finding');
    setShowAiModal(true);
    try {
      const res = await orchFetch(`/ai/analyze-finding/${findingId}/sync`, {
        method: 'POST',
        body: JSON.stringify({ engagement_id: id }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setAiResult({ error: err.detail || `AI analysis failed (${res.status})` });
      } else {
        setAiResult(await res.json());
      }
    } catch (err) {
      setAiResult({ error: 'Failed to connect to AI service' });
    }
    setAiAnalyzing(null);
  }, [orchFetch, id]);

  const load = useCallback(async () => {
    try {
      const res = await apiFetch(`/engagements/${id}`);
      if (!res.ok) { navigate('/engagements'); return; }
      setEng(await res.json());

      const canFetchTesters = user?.isMsp || user?.groups?.includes('Client Admins');
      const [tRes, vtRes, rtRes, fRes, pbRes] = await Promise.all([
        canFetchTesters ? apiFetch('/users/testers') : Promise.resolve({ json: () => [] }),
        apiFetch('/vuln-templates'),
        apiFetch('/report-templates'),
        apiFetch(`/uploads/${id}`),
        apiFetch(`/playbook/engagement/${id}`)
      ]);
      // These are refused for read-only accounts. An error body reaching list
      // state blanks the page when it is later mapped over, so anything that is
      // not an array becomes an empty list.
      const asList = async (r) => {
        const d = await r.json().catch(() => null);
        return Array.isArray(d) ? d : [];
      };
      if (canFetchTesters) {
        setTesters(await asList(tRes));
      }
      setVulnTemplates(await asList(vtRes));
      setReportTemplates(await asList(rtRes));
      if (fRes.ok) setFiles(await asList(fRes));
      if (pbRes.ok) {
        const pbData = await pbRes.json();
        setPlaybookItems(pbData.items || []);
        setPlaybookSummary(pbData.summary || null);
        if (Object.keys(expandedCategories).length === 0) {
          const cats = {};
          (pbData.items || []).forEach(item => { cats[item.category] = true; });
          setExpandedCategories(cats);
        }
      }

      // Load generated reports
      try {
        const grRes = await apiFetch(`/reports/engagement/${id}`);
        if (grRes.ok) setGeneratedReports(await grRes.json());
      } catch {}

      // Load activity log for this engagement
      try {
        const alRes = await apiFetch(`/audit?entityId=${id}&limit=50`);
        if (alRes.ok) {
          const alData = await alRes.json();
          setActivityLog(Array.isArray(alData?.data) ? alData.data : []);
        }
      } catch {}

      // Load integration data (ticket links)
      try {
        const engData = await (await apiFetch(`/engagements/${id}`)).json();
        if (engData.client_id) {
          const [intRes, ticketRes] = await Promise.all([
            apiFetch(`/integrations/${engData.client_id}`),
            apiFetch(`/integrations/sync-status-bulk/${id}`)
          ]);
          if (intRes.ok) setClientIntegrations(await intRes.json());
          if (ticketRes.ok) setFindingTickets(await ticketRes.json());
        }
      } catch { /* integrations not available */ }
    } catch {}
  }, [id, apiFetch, navigate, user]);

  useEffect(() => { load(); }, [load]);

  // Asked once on mount rather than lazily like the report modal does, because
  // the reports table needs the answer before it draws its action buttons.
  useEffect(() => {
    let cancelled = false;
    apiFetch('/reports/capabilities')
      .then(r => (r.ok ? r.json() : null))
      .then(caps => { if (!cancelled && caps) setGotenbergEnabled(!!caps.gotenbergEnabled); })
      .catch(() => { /* leave it off: better a missing button than one that 503s */ });
    return () => { cancelled = true; };
  }, [apiFetch]);

  useEffect(() => {
    if (!eng?.client_id || !canEditBilling) return;
    // includeId so a SoW that has since been completed still comes back: a
    // <select> whose value matches no option renders as "-- None --", which
    // made an engagement on a closed SoW look unassigned while the form state
    // still held the real id.
    apiFetch(`/engagements/sows-for-client/${eng.client_id}`
             + (eng.sow_id ? `?includeId=${encodeURIComponent(eng.sow_id)}` : ''))
      .then(r => r.json()).then(sows => {
        setBillingSows(sows);
        setBillingForm({ sowId: eng.sow_id || '', sowCredits: parseFloat(eng.sow_credits) || 1 });
        setBillingDirty(false);
      }).catch(() => setBillingSows([]));
  }, [eng?.client_id, eng?.sow_id, eng?.sow_credits, canEditBilling, apiFetch]);

  const saveBilling = async () => {
    setBillingSaving(true);
    try {
      const res = await apiFetch(`/billing/${eng.id}/assign-sow`, {
        method: 'PUT',
        body: JSON.stringify({ sowId: billingForm.sowId || null, sowCredits: parseFloat(billingForm.sowCredits) || 1 })
      });
      if (!res.ok) {
        const err = await res.json();
        alert(err.error || 'Failed to update');
      } else {
        setBillingDirty(false);
        load();
      }
    } catch { alert('Failed to update billing'); }
    setBillingSaving(false);
  };

  const loadCredentials = useCallback(async () => {
    try {
      const res = await apiFetch(`/engagements/${id}/credentials`);
      if (res.ok) setCredentials(await res.json());
    } catch {}
  }, [id, apiFetch]);

  useEffect(() => { loadCredentials(); }, [loadCredentials]);

  const loadEngNotes = useCallback(async () => {
    try {
      const res = await apiFetch(`/engagements/${id}/notes`);
      if (res.ok) setEngNotes(await res.json());
    } catch {}
  }, [id, apiFetch]);

  useEffect(() => { loadEngNotes(); }, [loadEngNotes]);

  const addEngNote = async () => {
    if (!newNote.trim()) return;
    try {
      const res = await apiFetch(`/engagements/${id}/notes`, { method: 'POST', body: JSON.stringify({ content: newNote }) });
      if (res.ok) {
        setNewNote('');
        loadEngNotes();
      } else {
        const err = await res.json();
        alert(err.error || 'Failed to add note');
      }
    } catch { alert('Failed to add note'); }
  };

  const deleteEngNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    await apiFetch(`/engagements/${id}/notes/${noteId}`, { method: 'DELETE' });
    loadEngNotes();
  };

  // Which credential is being edited, if any. The stored secret cannot be
  // read back for display, so editing starts with an empty secret field and an
  // empty one means "keep the current secret".
  const [editingCredId, setEditingCredId] = useState(null);

  const openAddCredential = () => {
    setEditingCredId(null);
    setNewCredential({ label: '', credential_type: 'bearer_token', username: '', secret: '', extra_config: {} });
    setShowCredentialModal(true);
  };

  const openEditCredential = (cred) => {
    setEditingCredId(cred.id);
    setNewCredential({
      label: cred.label || '',
      credential_type: cred.credential_type,
      // The username is encrypted too, so it is left blank and only sent when
      // the user types a replacement.
      username: '',
      secret: '',
      extra_config: cred.extra_config || {},
      is_active: cred.is_active !== false,
    });
    setShowCredentialModal(true);
  };

  const saveCredential = async () => {
    const body = { ...newCredential };
    if (!body.label) { alert('Label is required'); return; }
    if (!editingCredId && !body.secret) { alert('Label and secret/password are required'); return; }
    // On an edit, blank fields mean "leave this as it is", so they are dropped
    // rather than sent as empty strings the server would store.
    if (editingCredId) {
      if (!body.secret) delete body.secret;
      if (!body.username) delete body.username;
    }
    try {
      const res = await apiFetch(
        editingCredId ? `/engagements/${id}/credentials/${editingCredId}` : `/engagements/${id}/credentials`,
        { method: editingCredId ? 'PUT' : 'POST', body: JSON.stringify(body) }
      );
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        alert(errData.error || `Failed to save credential (HTTP ${res.status})`);
        return;
      }
      setNewCredential({ label: '', credential_type: 'bearer_token', username: '', secret: '', extra_config: {} });
      setEditingCredId(null);
      setShowCredentialModal(false);
      loadCredentials();
    } catch (err) {
      alert('Failed to save credential: ' + err.message);
    }
  };

  const [copiedCredId, setCopiedCredId] = useState(null);
  const copyCredentialField = async (credId, field) => {
    try {
      const res = await apiFetch(`/engagements/${id}/credentials/decrypted`);
      if (!res.ok) { alert('Failed to decrypt credentials'); return; }
      const creds = await res.json();
      const cred = creds.find(c => c.id === credId);
      if (cred) {
        const textToCopy = field === 'user' ? cred.username : field === 'pass' ? cred.secret : (cred.username ? `${cred.username}:${cred.secret}` : cred.secret);
        await navigator.clipboard.writeText(textToCopy);
        setCopiedCredId(`${credId}-${field}`);
        setTimeout(() => setCopiedCredId(null), 2000);
      }
    } catch (err) {
      alert('Failed to copy: ' + err.message);
    }
  };

  const deleteCredential = async (credId) => {
    if (!window.confirm('Remove this credential?')) return;
    const res = await apiFetch(`/engagements/${id}/credentials/${credId}`, { method: 'DELETE' });
    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      alert(errData.error || `Failed to remove credential (HTTP ${res.status})`);
      return;
    }
    loadCredentials();
  };

  const updateEngagement = async (updates) => {
    try {
      const res = await apiFetch(`/engagements/${id}`, { method: 'PUT', body: JSON.stringify(updates) });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        alert(errData.error || `Failed to update engagement (HTTP ${res.status})`);
        return;
      }
      load();
    } catch (err) {
      alert('Failed to update engagement: ' + err.message);
    }
  };

  const openLinkApplication = async () => {
    setLinkAppError('');
    setLinkAppId('');
    setShowLinkAppModal(true);
    try {
      const res = await apiFetch(`/applications/by-client/${eng.client_id}`);
      const d = await res.json().catch(() => null);
      setLinkableApps(Array.isArray(d) ? d : []);
      if (!res.ok) setLinkAppError(d?.error || 'Could not load applications');
    } catch {
      setLinkableApps([]);
      setLinkAppError('Could not load applications');
    }
  };

  const linkApplication = async () => {
    if (!linkAppId) return;
    setLinkingApp(true);
    setLinkAppError('');
    const res = await apiFetch(`/engagements/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ applicationId: linkAppId }),
    });
    setLinkingApp(false);
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      setLinkAppError(d.error || `Could not link the application (HTTP ${res.status})`);
      return;
    }
    setShowLinkAppModal(false);
    load();
  };

  const addScope = async () => {
    const res = await apiFetch(`/engagements/${id}/scope`, { method: 'POST', body: JSON.stringify(scopeForm) });
    // The response was discarded, which was survivable while the route
    // accepted anything. It now rejects an unknown scope type and an
    // unparseable address, and silently closing the modal on a 400 would
    // throw away what was typed and look exactly like a save.
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      window.alert(body?.error || `Could not add scope item (HTTP ${res.status})`);
      return;
    }
    setScopeForm({});
    setShowScopeModal(false);
    load();
  };

  // ─── Scope from the Networks inventory ─────────────────────────────
  //
  // A network or segmentation engagement scopes itself from addresses the
  // client already has on record, rather than having them retyped — which is
  // how the same range ends up in scope three different ways across three
  // engagements. A new address can also be added from here, which registers
  // it in the inventory AND puts it in scope in one step, because being made
  // to leave, create it, and come back is why people retype.
  const openNetworkPicker = async () => {
    setNetworkError('');
    setSelectedNetworkIds([]);
    setNewNetwork({ name: '', value: '', purpose: '', pci: 'false' });
    setPickerFilter({ search: '', tag: '', pciOnly: false });
    setShowNetworkPicker(true);
    try {
      const res = await apiFetch(`/networks/by-client/${eng.client_id}`);
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        setNetworkError(body?.error || `Could not load networks (HTTP ${res.status})`);
        setNetworkOptions([]);
        return;
      }
      setNetworkOptions(await res.json());
    } catch (e) {
      setNetworkError(String(e.message || e));
      setNetworkOptions([]);
    }
  };

  const addSelectedNetworks = async () => {
    if (!selectedNetworkIds.length) return;
    setNetworkBusy(true); setNetworkError('');
    try {
      const res = await apiFetch(`/engagements/${id}/scope/from-networks`, {
        method: 'POST', body: JSON.stringify({ networkIds: selectedNetworkIds }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) {
        setNetworkError(body?.error || `Could not add to scope (HTTP ${res.status})`);
        return;
      }
      // Entries already in scope are reported rather than silently dropped,
      // so picking a range twice does not look like a failed click.
      if (body?.skipped) {
        window.alert(`${body.addedCount} added. ${body.skipped} already in scope.`);
      }
      setShowNetworkPicker(false);
      load();
    } catch (e) {
      setNetworkError(String(e.message || e));
    } finally {
      setNetworkBusy(false);
    }
  };

  const createAndAddNetwork = async () => {
    if (!newNetwork.name.trim() || !newNetwork.value.trim()) {
      setNetworkError('A name and an IP address or CIDR range are both required');
      return;
    }
    setNetworkBusy(true); setNetworkError('');
    try {
      const made = await apiFetch('/networks', {
        method: 'POST',
        body: JSON.stringify({
          clientId: eng.client_id,
          name: newNetwork.name.trim(),
          value: newNetwork.value.trim(),
          purpose: newNetwork.purpose || null,
          pci: newNetwork.pci === 'true',
        }),
      });
      const row = await made.json().catch(() => null);
      if (!made.ok) {
        setNetworkError(row?.error || `Could not create the network (HTTP ${made.status})`);
        return;
      }
      const added = await apiFetch(`/engagements/${id}/scope/from-networks`, {
        method: 'POST', body: JSON.stringify({ networkIds: [row.id] }),
      });
      if (!added.ok) {
        const b = await added.json().catch(() => null);
        // The inventory entry exists even though the scope add failed, so say
        // so rather than leaving the user to wonder whether to create it again.
        setNetworkError((b?.error || 'Could not add it to scope') +
          ' — the entry was saved to the inventory, so add it from the list above.');
        const refreshed = await apiFetch(`/networks/by-client/${eng.client_id}`);
        if (refreshed.ok) setNetworkOptions(await refreshed.json());
        return;
      }
      setShowNetworkPicker(false);
      load();
    } catch (e) {
      setNetworkError(String(e.message || e));
    } finally {
      setNetworkBusy(false);
    }
  };

  // The scope as a target file. Fetched rather than linked because the route
  // needs the Authorization header, and putting a token in a URL would leave
  // it in history and logs.
  const exportTargets = async () => {
    try {
      const res = await apiFetch(`/engagements/${id}/scope/export.txt`);
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        window.alert(body?.error || `Could not export targets (HTTP ${res.status})`);
        return;
      }
      const text = await res.text();
      const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(eng.name || 'engagement').replace(/[^a-zA-Z0-9._-]+/g, '-')}-targets.txt`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      window.alert(String(e.message || e));
    }
  };

  const deleteScope = async (scopeId) => {
    if (!window.confirm('Remove this scope item?')) return;
    await apiFetch(`/engagements/${id}/scope/${scopeId}`, { method: 'DELETE' });
    load();
  };

  /**
   * The finding form's own shape, built from an API row.
   *
   * The form is camelCase; the API row is snake_case. Seeding the state with
   * the raw row left BOTH spellings in one object, and four controls read the
   * snake key first while their onChange wrote the camel one -- so a keystroke
   * landed in a key the input did not display and React re-rendered the stored
   * value straight back. CVSS score, CVSS vector, directly-exploitable and
   * affected endpoints could not be typed into at all.
   *
   * `??` falls through on null, which is why an empty box still accepted
   * typing and a filled one did not: the fields that refused to change were
   * exactly the ones that already had a value -- every field a template fills
   * in, which is why this showed up first on findings created from one.
   *
   * Nulls are carried through rather than flattened to '': each control
   * supplies its own display fallback, and saveFinding distinguishes a value
   * that was never set from one the tester cleared on purpose.
   */
  const findingFormFrom = (row) => {
    const r = row || {};
    return {
      title: r.title,
      severity: r.severity,
      status: r.status,
      cvssScore: r.cvss_score,
      cvssTags: r.cvss_tags,
      directExploitability: r.direct_exploitability,
      owaspCategory: r.owasp_category,
      cwe: r.cwe,
      remediationEffort: r.remediation_effort,
      details: r.details,
      affectedEndpoints: r.affected_endpoints,
      observation: r.observation,
      stepsToReproduce: r.steps_to_reproduce,
      recommendation: r.recommendation,
      references: r.references,
    };
  };

  const saveFinding = async () => {
    // findingFormFrom() guarantees the form holds one spelling -- the camelCase
    // one the request body uses -- so a field the user never touched still
    // carries the stored value rather than undefined. That matters because the
    // columns the update assigns without COALESCE (cvss_score,
    // direct_exploitability, affected_endpoints) would otherwise be wiped by
    // the act of opening a finding and saving it.
    const pick = (key) => findingForm[key] ?? null;
    // Number(), not parseFloat(...) || null: a CVSS of 0.0 is a real score and
    // || null turned it into "no score".
    const rawScore = pick('cvssScore');
    const parsedScore = rawScore === null || rawScore === '' ? null : Number(rawScore);

    const body = {
      engagementId: id,
      title: findingForm.title,
      cvssScore: Number.isFinite(parsedScore) ? parsedScore : null,
      cvssTags: pick('cvssTags'),
      severity: findingForm.severity,
      directExploitability: pick('directExploitability'),
      // '' is meaningful: it clears the classification. These three are the
      // ones the update treats as three-state, and the form now always has a
      // key for each, so an untouched one round-trips its stored value instead
      // of arriving as a clear.
      owaspCategory: pick('owaspCategory'),
      cwe: pick('cwe'),
      remediationEffort: pick('remediationEffort'),
      details: findingForm.details,
      affectedEndpoints: pick('affectedEndpoints'),
      observation: findingForm.observation,
      stepsToReproduce: pick('stepsToReproduce'),
      recommendation: findingForm.recommendation,
      references: findingForm.references,
      status: findingForm.status || 'Open'
    };
    if (editFinding) {
      const res = await apiFetch(`/findings/${editFinding.id}`, { method: 'PUT', body: JSON.stringify(body) });
      // A finding describing an XSS or an injection carries the payload in its
      // details, which is exactly what the firewall in front of the API
      // refuses. Saying so beats "something went wrong".
      if (!res.ok) { alert(await apiErrorMessage(res, 'Failed to save finding')); return; }
      setShowFindingModal(false);
      setEditFinding(null);
      setFindingForm({});
      setEvidence([]);
    } else {
      // New finding: save then switch to edit mode so user can upload screenshots
      const res = await apiFetch('/findings', { method: 'POST', body: JSON.stringify(body) });
      if (!res.ok) { alert(await apiErrorMessage(res, 'Failed to save finding')); return; }
      const newFinding = await res.json();
      setEditFinding(newFinding);
      setFindingForm(findingFormFrom(newFinding));
    }
    load();
  };

  // Filling the form, not creating the finding. The old control posted to
  // /findings/from-template from the list page and the tester got a finished
  // finding they then had to open and edit; the observation and endpoints are
  // engagement-specific and always needed changing. Nothing is saved until
  // they press Save.
  //
  // One spelling per field, because the form is canonical -- see
  // findingFormFrom below.
  const applyTemplate = (t) => {
    if (!t) return;
    setFindingForm(f => ({
      ...f,
      title: t.title || '',
      severity: t.severity || f.severity || 'Medium',
      cvssScore: t.cvss_score ?? null,
      cvssTags: t.cvss_tags || '',
      directExploitability: t.direct_exploitability || 'No',
      details: t.details || '',
      observation: t.observation || '',
      recommendation: t.recommendation || '',
      references: t.references || '',
      owaspCategory: t.owasp_category || '',
      // The library carries a CWE, so it travels with the finding the way
      // the OWASP code does. Remediation effort has no library column, so it
      // starts empty -- written out rather than left undefined, which would
      // leave whatever the last finding had sitting in the box.
      cwe: t.cwe || '', remediationEffort: '',
      // The library's proof of concept, converted to the JSON array every
      // other path stores. Handing the raw text through looked right -- the
      // editor parses it on display -- but the editor only emits JSON when a
      // step is actually edited, so saving without touching it wrote plain
      // text. The DOCX renderer JSON.parses that field and renders per-step
      // evidence only inside the array branch, so a screenshot attached to a
      // step would have appeared in no report at all.
      stepsToReproduce: JSON.stringify(parseSteps(t.steps_to_reproduce || '')),
      // Deliberately NOT set: affected endpoints and status. Both belong to
      // the engagement in front of the tester, not to the library.
    }));
    // StepsToReproduce reads its value once, on mount, so without a new key
    // the steps list would keep whatever was there before the fill.
    setTemplateFillKey(k => k + 1);
  };

  const importBurpXml = async () => {
    if (!burpFile) return;
    setBurpImporting(true);
    setBurpResult(null);
    try {
      const formData = new FormData();
      formData.append('file', burpFile);
      const token = sessionStorage.getItem('token');
      const res = await fetch(`/api/burp/import-findings/${id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      if (res.ok) {
        setBurpResult(data);
        load();
      } else {
        setBurpResult({ error: data.error || 'Failed to import Burp XML' });
      }
    } catch {
      setBurpResult({ error: 'Failed to import Burp XML file' });
    }
    setBurpImporting(false);
  };

  const deleteFinding = async (fId) => {
    if (!window.confirm('Delete this finding?')) return;
    await apiFetch(`/findings/${fId}`, { method: 'DELETE' });
    load();
  };

  const markRemediated = async (fId) => {
    await apiFetch(`/findings/${fId}`, { method: 'PUT', body: JSON.stringify({ status: 'Retest' }) });
    load();
  };

  const submitRemediation = async (findingId) => {
    if (!remediateNotes.trim()) return;
    try {
      const res = await apiFetch(`/findings/${findingId}/remediate`, {
        method: 'POST', body: JSON.stringify({ notes: remediateNotes })
      });
      if (res.ok) {
        setShowRemediateForm(false);
        setRemediateNotes('');
        load();
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data.error || 'Failed to submit remediation');
      }
    } catch {}
  };

  const requestAiSuggest = async () => {
    if (!findingForm.title || findingForm.title.length < 5) return;
    setAiLoading(true);
    setAiError(null);
    setAiSuggestions(null);
    try {
      const res = await orchFetch('/ai/finding-assist', {
        method: 'POST',
        body: JSON.stringify({
          title: findingForm.title,
          details: findingForm.details || null,
          observation: findingForm.observation || null,
          severity: findingForm.severity || null,
          engagement_type: eng.engagement_type || null,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setAiSuggestions(data);
        if (!data.ai_available && data.matching_templates?.length > 0) {
          setAiError('AI service unavailable — showing matching templates only.');
        }
      } else {
        setAiError('AI service unavailable.');
      }
    } catch {
      setAiError('Could not reach AI service.');
    } finally {
      setAiLoading(false);
    }
  };

  const applyAiSuggestion = (formKey, value) => {
    setFindingForm(f => ({ ...f, [formKey]: value }));
  };

  // Integration sync functions
  const syncFinding = async (findingId) => {
    setSyncing(findingId);
    try {
      const res = await apiFetch(`/integrations/sync/${findingId}`, { method: 'POST' });
      const data = await res.json();
      if (res.ok) {
        load();
      } else {
        alert(data.error || 'Sync failed');
      }
    } catch (err) {
      alert('Sync failed: ' + err.message);
    }
    setSyncing(null);
  };

  const syncAllFindings = async () => {
    setBulkSyncing(true);
    try {
      const res = await apiFetch(`/integrations/sync-engagement/${id}`, { method: 'POST' });
      const data = await res.json();
      if (res.ok) {
        alert(`Sync complete: ${data.created || 0} created, ${data.updated || 0} updated, ${data.errors || 0} errors`);
        load();
      } else {
        alert(data.error || 'Bulk sync failed');
      }
    } catch (err) {
      alert('Bulk sync failed: ' + err.message);
    }
    setBulkSyncing(false);
  };

  const pullTicketStatuses = async () => {
    setPulling(true);
    try {
      const res = await apiFetch(`/integrations/pull-status/${id}`, { method: 'POST' });
      const data = await res.json();
      if (res.ok) {
        load();
      } else {
        alert(data.error || 'Pull failed');
      }
    } catch (err) {
      alert('Pull failed: ' + err.message);
    }
    setPulling(false);
  };

  const getTicketsForFinding = (findingId) => {
    return findingTickets.filter(t => t.finding_id === findingId);
  };

  const hasActiveIntegrations = clientIntegrations.some(i => i.is_active);

  // Playbook functions
  const initializePlaybook = async () => {
    setPlaybookLoading(true);
    try {
      const res = await apiFetch(`/playbook/engagement/${id}/initialize`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setPlaybookItems(data.items || []);
        setPlaybookSummary(data.summary || null);
        const cats = {};
        (data.items || []).forEach(item => { cats[item.category] = true; });
        setExpandedCategories(cats);
      } else {
        const d = await res.json();
        alert(d.error || 'Failed to initialize playbook');
      }
    } catch { alert('Failed to initialize playbook'); }
    setPlaybookLoading(false);
  };

  const updatePlaybookItem = async (itemId, updates) => {
    try {
      const res = await apiFetch(`/playbook/engagement/${id}/items/${itemId}`, {
        method: 'PUT', body: JSON.stringify(updates)
      });
      if (res.ok) {
        const updated = await res.json();
        setPlaybookItems(prev => prev.map(i => i.id === itemId ? { ...i, ...updated } : i));
        // Recompute summary
        setPlaybookItems(prev => {
          const items = prev.map(i => i.id === itemId ? { ...i, ...updated } : i);
          const total = items.length;
          const open = items.filter(i => i.status === 'Open').length;
          const vulnerable = items.filter(i => i.status === 'Vulnerable').length;
          const not_vulnerable = items.filter(i => i.status === 'Not Vulnerable').length;
          const flagged = items.filter(i => i.status === 'Flag for Follow Up').length;
          const progress_percent = total > 0 ? Math.round(((total - open) / total) * 100) : 0;
          setPlaybookSummary({ total, open, vulnerable, not_vulnerable, flagged, progress_percent });
          return items;
        });
        // If a finding was auto-created when marking Vulnerable, reload engagement to show it
        if (updated.createdFinding) {
          load();
        }
      }
    } catch { alert('Failed to update playbook item'); }
  };

  const deletePlaybookItem = async (itemId) => {
    if (!window.confirm('Remove this test case from the playbook?')) return;
    await apiFetch(`/playbook/engagement/${id}/items/${itemId}`, { method: 'DELETE' });
    setPlaybookItems(prev => {
      const items = prev.filter(i => i.id !== itemId);
      const total = items.length;
      const open = items.filter(i => i.status === 'Open').length;
      const vulnerable = items.filter(i => i.status === 'Vulnerable').length;
      const not_vulnerable = items.filter(i => i.status === 'Not Vulnerable').length;
      const flagged = items.filter(i => i.status === 'Flag for Follow Up').length;
      const progress_percent = total > 0 ? Math.round(((total - open) / total) * 100) : 0;
      setPlaybookSummary({ total, open, vulnerable, not_vulnerable, flagged, progress_percent });
      return items;
    });
  };


  // Group playbook items by category
  const playbookByCategory = playbookItems.reduce((acc, item) => {
    (acc[item.category] = acc[item.category] || []).push(item);
    return acc;
  }, {});

  const statusColors = {
    'Open': '#94a3b8',
    'Vulnerable': '#ef4444',
    'Not Vulnerable': '#22c55e',
    'Flag for Follow Up': '#f59e0b'
  };

  const openReportModal = async () => {
    setIncludeCertificate(false);
    const defaultTmpl = reportTemplates.find(t => t.is_default) || reportTemplates[0];
    setSelectedTemplateId(defaultTmpl?.id || '');
    // Which formats this deployment will produce (PDF is env-gated on the backend).
    let caps = { formats: ['docx'], defaultFormat: 'docx' };
    try {
      const res = await apiFetch('/reports/capabilities');
      if (res.ok) caps = await res.json();
    } catch (err) { /* fall back to docx-only */ }
    setReportFormats(caps.formats || ['docx']);
    setSelectedFormats([caps.defaultFormat || 'docx']);

    // Pre-fill with the name the backend would have chosen anyway, asked for
    // rather than rebuilt here -- the convention lives in reports.js and a
    // second copy would drift from it. On failure the field is left blank,
    // which the backend reads as "use the generated name", so the dialog still
    // works if this call does not.
    let suggested = '';
    try {
      const nameRes = await apiFetch(`/reports/suggested-name/${id}`);
      if (nameRes.ok) suggested = (await nameRes.json()).name || '';
    } catch { /* leave blank; the backend falls back */ }
    setSuggestedReportName(suggested);
    setReportName(suggested);
    const maxVer = generatedReports.reduce((m, r) => Math.max(m, parseFloat(r.version) || 0), 0);
    setReportVersion(String(maxVer ? maxVer + 1 : 1));
    setShowReportModal(true);
  };

  const downloadGenerated = async (url, fileName) => {
    const dlRes = await apiFetch(url);
    if (!dlRes.ok) return;
    const blob = await dlRes.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(objectUrl);
  };

  const generateReport = async (templateId, formats) => {
    setGenerating(true);
    try {
      const res = await apiFetch(`/reports/generate/${id}`, {
        method: 'POST', body: JSON.stringify({
          templateId,
          formats,
          // The single-value field the backend still accepts. Sent alongside so
          // that a newer dialog talking to a backend that has not been
          // redeployed yet still produces the leading format rather than
          // falling through to that backend's PDF default.
          format: formats.includes('docx') ? 'docx' : 'pdf',
          version: parseFloat(reportVersion) || undefined,
          includeCertificate,
          // Blank means "use the generated name" on the backend, so an
          // untouched field behaves exactly as before this existed.
          reportName: reportName.trim() || undefined,
        })
      });
      const data = await res.json();
      if (res.ok) {
        setShowReportModal(false);
        load();
        // A run can now produce two files. `reports` lists them; the older
        // single-file shape is the fallback for a backend without it -- and
        // that shape carries its converted PDF separately, under `pdf`, so a
        // dialog running ahead of its backend still hands over both files
        // rather than leaving one to be found in the Reports tab.
        const produced = Array.isArray(data.reports) && data.reports.length
          ? data.reports
          : [
              { download_url: data.download_url, file_name: data.report?.file_name },
              ...(data.pdf ? [{ download_url: data.pdf.download_url, file_name: data.pdf.file_name }] : []),
            ];
        for (const f of produced) {
          if (!f?.download_url || !f?.file_name) continue;
          await downloadGenerated(f.download_url, f.file_name);
          // Browsers treat a burst of programmatic downloads as one event and
          // can drop the later ones; a short gap keeps them distinct.
          if (produced.length > 1) await new Promise(r => setTimeout(r, 400));
        }
        // A partial success -- Word saved, PDF conversion failed -- comes back
        // 200 with this, and would otherwise be invisible.
        if (Array.isArray(data.warnings) && data.warnings.length) {
          alert(data.warnings.join('\n'));
        }
      } else {
        alert(data.error || 'Report generation failed');
      }
    } catch (err) {
      alert('Report generation failed: ' + err.message);
    } finally {
      setGenerating(false);
    }
  };

  const downloadReport = async (reportId, fileName) => {
    try {
      const res = await apiFetch(`/reports/${reportId}/download`);
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch {}
  };

  // Convert a Word version into a password-protected PDF. The password is not
  // in this response -- it is read back through the modal, so that every sight
  // of it is audited, including the first.
  const protectReport = async (reportId, fileName) => {
    if (!window.confirm(`Create a password-protected PDF from this Word version?\n\n${fileName}\n\nA password is generated and stored; you choose who may see it.`)) return;
    setProtectingReport(reportId);
    try {
      const res = await apiFetch(`/reports/${reportId}/protect`, { method: 'POST' });
      if (!res.ok) { alert(await apiErrorMessage(res, 'Could not create the protected PDF')); return; }
      const data = await res.json();
      await load();
      // Straight into the password view: the tester has just made a file
      // nobody can open yet, so the password is the next thing they need.
      openPasswordModal({ id: data.report.id, file_name: data.report.file_name });
    } catch {
      alert('Could not create the protected PDF');
    } finally {
      setProtectingReport(null);
    }
  };

  const openPasswordModal = async (report) => {
    setPasswordModal(report);
    setPasswordData(null);
    setPasswordShares(null);
    setShareUserId('');
    setPasswordBusy(true);
    try {
      const res = await apiFetch(`/reports/${report.id}/password`);
      if (!res.ok) { alert(await apiErrorMessage(res, 'Could not read the password')); setPasswordModal(null); return; }
      setPasswordData(await res.json());
      if (isMspPenTester) await loadPasswordShares(report.id);
    } catch {
      alert('Could not read the password');
      setPasswordModal(null);
    } finally {
      setPasswordBusy(false);
    }
  };

  const loadPasswordShares = async (reportId) => {
    const res = await apiFetch(`/reports/${reportId}/password/shares`);
    if (res.ok) setPasswordShares(await res.json());
  };

  const sharePassword = async () => {
    if (!passwordModal || !shareUserId) return;
    setPasswordBusy(true);
    try {
      const res = await apiFetch(`/reports/${passwordModal.id}/password/shares`, {
        method: 'POST', body: JSON.stringify({ userId: shareUserId }),
      });
      if (!res.ok) { alert(await apiErrorMessage(res, 'Could not share the password')); return; }
      setShareUserId('');
      await loadPasswordShares(passwordModal.id);
      load();
    } finally { setPasswordBusy(false); }
  };

  const revokePasswordShare = async (userId, name) => {
    if (!passwordModal) return;
    if (!window.confirm(`Remove ${name}'s access to this password?\n\nThey keep any copy they already wrote down -- revoking stops them reading it here again.`)) return;
    setPasswordBusy(true);
    try {
      const res = await apiFetch(`/reports/${passwordModal.id}/password/shares/${userId}`, { method: 'DELETE' });
      if (!res.ok) { alert(await apiErrorMessage(res, 'Could not revoke the share')); return; }
      await loadPasswordShares(passwordModal.id);
      load();
    } finally { setPasswordBusy(false); }
  };

  const recordDelivery = async () => {
    if (!showDeliveryModal) return;
    try {
      await apiFetch(`/reports/${showDeliveryModal}/deliveries`, {
        method: 'POST', body: JSON.stringify(deliveryForm)
      });
      setShowDeliveryModal(null);
      setDeliveryForm({ deliveryMethod: 'email', recipientName: '', recipientEmail: '', notes: '' });
      load();
    } catch {}
  };

  const revokeCertificate = async () => {
    if (!showRevokeModal) return;
    try {
      await apiFetch(`/reports/${showRevokeModal}/revoke-certificate`, {
        method: 'POST', body: JSON.stringify({ reason: revokeReason })
      });
      setShowRevokeModal(null);
      setRevokeReason('');
      load();
    } catch {}
  };

  /**
   * Upload report files. Shared by the file picker and the drop zone, so both
   * routes behave identically.
   *
   * Files go up one at a time rather than in parallel: the server assigns each
   * upload the next version number and flips is_current, so concurrent uploads
   * would race over both.
   */
  const uploadReportFiles = async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (list.length === 0) return;

    // The picker enforces this through its accept attribute; a drop does not,
    // so the check lives here where both paths pass through.
    const rejected = list.filter(f => !REPORT_UPLOAD_EXTENSIONS.some(ext => f.name.toLowerCase().endsWith(ext)));
    const accepted = list.filter(f => !rejected.includes(f));
    if (rejected.length > 0) {
      setReportUploadError(
        `Not a report file: ${rejected.map(f => f.name).join(', ')}. ` +
        `Accepted types: ${REPORT_UPLOAD_EXTENSIONS.join(', ')}`
      );
    } else {
      setReportUploadError('');
    }
    if (accepted.length === 0) return;

    setUploadingReport(true);
    const failures = [];
    try {
      for (const file of accepted) {
        const formData = new FormData();
        formData.append('file', file);
        const token = sessionStorage.getItem('token');
        try {
          const res = await fetch(`/api/reports/upload/${id}`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formData,
          });
          if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            failures.push(`${file.name}: ${data.error || `HTTP ${res.status}`}`);
          }
        } catch {
          failures.push(`${file.name}: upload failed`);
        }
      }
    } finally {
      setUploadingReport(false);
      // Reload even after a partial failure — whatever did land should show.
      load();
    }
    if (failures.length > 0) setReportUploadError(failures.join(' · '));
  };

  const handleReportFileUpload = async (e) => {
    await uploadReportFiles(e.target.files);
    e.target.value = '';
  };

  const handleReportDrop = async (e) => {
    e.preventDefault();
    setReportDragDepth(0);
    if (!canReport || uploadingReport) return;
    await uploadReportFiles(e.dataTransfer?.files);
  };

  /**
   * Promote a report to current. The server clears the flag from every other
   * version and sets it on both formats of this one, so the list is reloaded
   * rather than patched locally.
   */
  const markReportCurrent = async (reportId, fileName) => {
    if (!window.confirm(`Mark this as the current report?

${fileName}

The Current flag is removed from every other version.`)) return;
    const res = await apiFetch(`/reports/${reportId}/current`, { method: 'PATCH' });
    if (!res.ok) {
      const data = await res.json().catch(() => null);
      alert(data?.error || `Could not mark as current (HTTP ${res.status})`);
      return;
    }
    load();
  };

  const deleteReport = async (reportId, fileName) => {
    if (!window.confirm(`Are you sure you want to permanently delete this report?\n\n${fileName}\n\nThis will remove the file from storage and cannot be undone.`)) return;
    try {
      await apiFetch(`/reports/${reportId}`, { method: 'DELETE' });
      load();
    } catch {}
  };

  const saveReportVersion = async (reportId) => {
    const v = parseFloat(editReportVersionVal);
    if (!v || v <= 0) return;
    try {
      await apiFetch(`/reports/${reportId}/version`, {
        method: 'PATCH', body: JSON.stringify({ version: v })
      });
      setEditingReportVersion(null);
      load();
    } catch { alert('Failed to update version'); }
  };

  const loadDeliveries = async (reportId) => {
    try {
      const res = await apiFetch(`/reports/${reportId}/deliveries`);
      if (res.ok) {
        const data = await res.json();
        setReportDeliveries(prev => ({ ...prev, [reportId]: data }));
      }
    } catch {}
  };

  const handleFileUpload = async (e) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles?.length) return;
    setUploading(true);
    try {
      const formData = new FormData();
      for (let i = 0; i < selectedFiles.length; i++) {
        formData.append('files', selectedFiles[i]);
      }
      formData.append('category', uploadCategory);
      const res = await apiFetch(`/uploads/${id}`, { method: 'POST', body: formData, isFormData: true });
      if (!res.ok) {
        const text = await res.text();
        try { const d = JSON.parse(text); alert(d.error || `Upload failed (HTTP ${res.status})`); }
        catch { alert(res.status === 413 ? 'File too large. Increase nginx client_max_body_size.' : `Upload failed (HTTP ${res.status})`); }
      } else { load(); }
    } catch (err) { alert(err.message); }
    setUploading(false);
    e.target.value = '';
  };

  const handleReportUpload = async (e) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles?.length) return;
    setUploadingReport(true);
    try {
      const formData = new FormData();
      for (let i = 0; i < selectedFiles.length; i++) {
        formData.append('files', selectedFiles[i]);
      }
      formData.append('category', reportUploadCategory);
      const res = await apiFetch(`/uploads/${id}`, { method: 'POST', body: formData, isFormData: true });
      if (!res.ok) {
        const text = await res.text();
        try { const d = JSON.parse(text); alert(d.error || `Upload failed (HTTP ${res.status})`); }
        catch { alert(res.status === 413 ? 'File too large. Increase nginx client_max_body_size.' : `Upload failed (HTTP ${res.status})`); }
      } else { load(); }
    } catch (err) { alert(err.message); }
    setUploadingReport(false);
    e.target.value = '';
  };

  const downloadFile = async (fileId, originalName) => {
    try {
      const res = await apiFetch(`/uploads/${id}/download/${fileId}`);
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = originalName;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) { alert(err.message); }
  };

  const deleteFile = async (fileId) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await apiFetch(`/uploads/${id}/${fileId}`, { method: 'DELETE' });
      load();
    } catch (err) { alert(err.message); }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  // Evidence / Screenshot helpers
  const loadEvidence = async (findingId) => {
    if (!findingId) { setEvidence([]); return; }
    try {
      const res = await apiFetch(`/findings/${id}/${findingId}/evidence`);
      if (res.ok) setEvidence(await res.json());
      else setEvidence([]);
    } catch { setEvidence([]); }
  };

  const uploadEvidence = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editFinding?.id) return;
    const caption = prompt('Enter a caption for this screenshot (optional):') || '';
    setEvidenceUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (caption) formData.append('caption', caption);
      const res = await apiFetch(`/findings/${id}/${editFinding.id}/evidence`, {
        method: 'POST', body: formData, isFormData: true
      });
      if (res.ok) await loadEvidence(editFinding.id);
      else { const d = await res.json(); alert(d.error); }
    } catch (err) { alert(err.message); }
    setEvidenceUploading(false);
    e.target.value = '';
  };

  const updateEvidenceCaption = async (evidenceId, caption) => {
    try {
      await apiFetch(`/findings/${id}/${editFinding.id}/evidence/${evidenceId}`, {
        method: 'PUT', body: JSON.stringify({ caption })
      });
      await loadEvidence(editFinding.id);
      setEditingCaption(null);
    } catch (err) { alert(err.message); }
  };

  const deleteEvidence = async (evidenceId) => {
    if (!window.confirm('Delete this screenshot?')) return;
    try {
      await apiFetch(`/findings/${id}/${editFinding.id}/evidence/${evidenceId}`, { method: 'DELETE' });
      await loadEvidence(editFinding.id);
    } catch (err) { alert(err.message); }
  };

  const reorderEvidence = async (evidenceId, direction) => {
    const idx = evidence.findIndex(e => e.id === evidenceId);
    if (idx < 0) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= evidence.length) return;
    try {
      await Promise.all([
        apiFetch(`/findings/${id}/${editFinding.id}/evidence/${evidence[idx].id}`, {
          method: 'PUT', body: JSON.stringify({ sortOrder: evidence[swapIdx].sort_order })
        }),
        apiFetch(`/findings/${id}/${editFinding.id}/evidence/${evidence[swapIdx].id}`, {
          method: 'PUT', body: JSON.stringify({ sortOrder: evidence[idx].sort_order })
        })
      ]);
      await loadEvidence(editFinding.id);
    } catch (err) { alert(err.message); }
  };

  if (!eng) return <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>;

  const findingCols = [
    { key: 'finding_uid', label: 'ID', render: v => v ? <span style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--text-secondary)' }}>{v}</span> : '—' },
    { key: 'title', label: 'Title' },
    { key: 'severity', label: 'Severity', render: v => <Badge severity={v}>{v}</Badge> },
    { key: 'cvss_score', label: 'CVSS', render: v => v || '—' },
    { key: 'status', label: 'Status', render: v => <Badge>{v || 'Open'}</Badge> },
    // Only once this engagement has actually been through a retest. On every
    // other engagement these three columns would be an empty one and two
    // copies of the columns beside them.
    ...(eng.in_retest_at ? [
      { key: 'original_severity', label: 'Was', render: v => v ? <Badge severity={v}>{v}</Badge> : '—' },
      { key: 'original_status', label: 'Was (status)', render: v => v ? <Badge>{v}</Badge> : '—' },
      // Blank until the retest is marked complete, which is the honest answer
      // while one is still running: the outcome is not decided yet.
      { key: 'retest_status', label: 'Retest result', render: v => v ? <Badge>{v}</Badge> : '—' },
    ] : []),
    ...(hasActiveIntegrations ? [{
      // Keyed on the finding UUID, so ordering by it means nothing.
      key: 'id', label: 'Tickets', sortable: false, render: (v) => {
        const tickets = getTicketsForFinding(v);
        if (tickets.length === 0) return <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>—</span>;
        return (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {tickets.map(t => (
              <a key={t.id} href={t.external_ticket_url} target="_blank" rel="noopener noreferrer"
                onClick={e => e.stopPropagation()}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                  textDecoration: 'none', cursor: 'pointer',
                  background: t.integration_type === 'jira' ? '#0052CC' : '#81B5A1',
                  color: t.integration_type === 'jira' ? '#fff' : '#000',
                }}>
                {t.integration_type === 'jira' ? '🔵' : '🟢'} {t.external_ticket_key}
                {t.sync_error && <span title={t.sync_error} style={{ color: '#fca5a5' }}>⚠</span>}
              </a>
            ))}
          </div>
        );
      }
    }] : []),
    { key: 'id', label: 'Actions', render: (v, row) => (
      <div style={{ display: 'flex', gap: 8 }}>
        {hasActiveIntegrations && getTicketsForFinding(v).length === 0 && (
          <Button variant="secondary" style={{ padding: '4px 12px', fontSize: 12, background: '#0052CC', color: '#fff', border: 'none' }}
            onClick={(e) => { e.stopPropagation(); syncFinding(v); }}
            disabled={syncing === v}>
            {syncing === v ? '⏳' : '🎫'} Export
          </Button>
        )}
        {canEdit && (
          <>
            <Button variant="secondary" style={{ padding: '4px 12px', fontSize: 12 }}
              onClick={(e) => { e.stopPropagation(); setEditFinding(row); setFindingForm(findingFormFrom(row)); loadEvidence(row.id); setShowFindingModal(true); }}>Edit</Button>
            <Button variant="danger" style={{ padding: '4px 12px', fontSize: 12 }}
              onClick={(e) => { e.stopPropagation(); deleteFinding(v); }}>Delete</Button>
          </>
        )}
      </div>
    )}
  ];

  const openEditModal = () => {
    setEngEditForm({
      name: eng.name || '', engagementType: eng.engagement_type || '', environmentType: eng.environment_type || '',
      testingApproach: eng.testing_approach || DEFAULT_APPROACH,
      testerId: eng.tester_id || '', startDate: eng.start_date?.split('T')[0] || '', endDate: eng.end_date?.split('T')[0] || '',
      crestRequired: eng.crest_required || false, pciRequired: eng.pci_required || false, thirdParty: eng.third_party || false,
      clientObligation: eng.client_obligation || false, clientObligationFor: eng.client_obligation_for || '',
      customerTrackingId: eng.customer_tracking_id || '', serviceOrder: eng.service_order || '',
      jiraProjectKey: eng.jira_project_key || '', billingAmount: eng.billing_amount || '', notes: eng.notes || '',
      companyOwned: eng.company_owned ? 'true' : 'false', companyHosted: eng.company_hosted ? 'true' : 'false',
      internetFacing: eng.internet_facing ? 'true' : 'false', paymentFunction: eng.payment_function ? 'true' : 'false',
      fileUploadFunction: eng.file_upload_function ? 'true' : 'false', ipWhitelistNeeded: eng.ip_whitelist_needed ? 'true' : 'false',
      scopingComments: eng.scoping_comments || '', testingRestrictions: eng.testing_restrictions || { ...DEFAULT_RESTRICTIONS },
      sowId: eng.sow_id || '', sowCredits: parseFloat(eng.sow_credits) || 1, singlePointOfContact: eng.single_point_of_contact || '',
      technicalManager: eng.technical_manager || '', followUpDate: eng.follow_up_date?.split('T')[0] || '',
      challengesDetails: eng.challenges_details || '',
      vendorId: eng.vendor_id || '',
    });
    apiFetch(`/engagements/sows-for-client/${eng.client_id}`
             + (eng.sow_id ? `?includeId=${encodeURIComponent(eng.sow_id)}` : ''))
      .then(r => r.json()).then(setEditSows).catch(() => setEditSows([]));
    apiFetch(`/client-vendors/${eng.client_id}`)
      .then(r => r.json()).then(setEditVendors).catch(() => setEditVendors([]));
    setShowEditEngModal(true);
  };

  const testerInitials = eng.tester_name ? eng.tester_name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase() : '';
  const tabCounts = {
    findings: eng.findings?.length || 0,
    scope: eng.scope_items?.length || 0,
    notes: engNotes.length,
  };
  const TABS = [
    { id: 'overview', label: 'Overview' },
    { id: 'findings', label: 'Findings', count: tabCounts.findings },
    { id: 'scope', label: 'Scope', count: tabCounts.scope },
    { id: 'reports', label: 'Reports', count: generatedReports.length || undefined },
    { id: 'documents', label: 'Documents', count: files.filter(f => f.category !== 'Report Draft' && f.category !== 'Final Report').length || undefined },
    { id: 'notes', label: 'Notes', count: tabCounts.notes },
    { id: 'activity', label: 'Activity', count: activityLog.length || undefined },
  ];

  return (
    <div>
      {/* Sticky Engagement Header */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: '16px 20px', marginBottom: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <button onClick={() => navigate('/engagements')} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 0, display: 'flex' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
              </button>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{eng.client_name}</span>
            </div>
            <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8, letterSpacing: '-0.3px' }}>{eng.name || eng.engagement_type}</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <Badge>{eng.status}</Badge>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{eng.engagement_type}</span>
              {eng.tester_name && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#4f8fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, fontWeight: 700, color: 'white' }}>{testerInitials}</div>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{eng.tester_name}</span>
                </div>
              )}
              {eng.start_date && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{eng.start_date.split('T')[0]} – {eng.end_date?.split('T')[0] || 'TBD'}</span>}
              {eng.sow_number && <span style={{ fontSize: 11, color: 'var(--text-muted)', background: 'var(--bg-input)', padding: '2px 8px', borderRadius: 6 }}>{eng.sow_number}</span>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            {canEdit && <Button variant="secondary" style={{ fontSize: 12, padding: '7px 14px' }} onClick={openEditModal}>Edit</Button>}
            {/* openReportModal, not a bare setShowReportModal: this header button
                opened the dialog without running any of its setup, so the template
                came up unselected and the version blank — and the name field would
                have been blank too. The Reports tab button has always called the
                opener; this one was skipping it. */}
            <Button variant="secondary" style={{ fontSize: 12, padding: '7px 14px' }}
              disabled={!canGenerateReport}
              title={reportBlockedReason || 'Generate a report for this engagement'}
              onClick={() => canGenerateReport && openReportModal()}>
              Generate Report
            </Button>
            {canEdit && <Button style={{ fontSize: 12, padding: '7px 14px' }} onClick={() => { setEditFinding(null); setFindingForm({ severity: 'Medium', status: 'Open', directExploitability: 'No' }); setEvidence([]); setShowFindingModal(true); }}>+ Add Finding</Button>}
            {/* Same launcher as the engagement drawer. Gated like the
                /agent-scanner route, and hidden once the engagement is closed
                out: findings raised then would land against a report that has
                already gone out. */}
            {user?.aiEnabled && canEdit && eng.status !== 'Complete' && eng.status !== 'Cancelled' && (
              <Button style={{
                fontSize: 12, padding: '7px 14px',
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              }} onClick={() => setLaunchAgent(true)}>
                <span>&#129302;</span> Send to AI Pentest Agent
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* An engagement of a type that tests one application, with none linked.
          Worth prompting about because nothing else says so: the engagement
          works normally, and only the compliance figures are wrong.

          Sits above the tab bar deliberately. It is an engagement-level
          condition, and while it lived inside the Scope tab it was invisible —
          the page opens on Findings, so nobody saw the prompt. */}
      {needsApplication && (
        <Card style={{ marginBottom: 16, borderLeft: '3px solid #eab308' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 280 }}>
              <strong style={{ fontSize: 14 }}>This engagement is not linked to an application</strong>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
                Testing only counts towards an application&rsquo;s compliance when the engagement says
                which application it covers. Until this is linked, the application it tested still
                reads as untested in Governance.
              </div>
            </div>
            {canLinkApplication ? (
              <Button onClick={openLinkApplication}>Link application</Button>
            ) : (
              <span style={{ fontSize: 13, color: 'var(--text-secondary)', alignSelf: 'center' }}>
                An Engagement Admin or Application Manager can link it.
              </span>
            )}
          </div>
        </Card>
      )}

      {/* Tab Bar */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border)', marginBottom: 20, background: 'var(--bg-card)', borderRadius: '0 0 10px 10px', padding: '0 20px' }}>
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '12px 16px', background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: activeTab === tab.id ? 600 : 500,
              color: activeTab === tab.id ? 'var(--primary)' : 'var(--text-muted)',
              borderBottom: activeTab === tab.id ? '2px solid var(--primary)' : '2px solid transparent',
              transition: 'all 0.15s', fontFamily: 'inherit',
            }}>
            {tab.label}{tab.count != null ? ` (${tab.count})` : ''}
          </button>
        ))}
      </div>

      {/* Tab: Overview */}
      {activeTab === 'overview' && (
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20, marginBottom: 24 }}>
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <div>
              <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Engagement Details</h2>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 14 }}>
            <div><strong>Tester:</strong> {eng.tester_name || 'Unassigned'}</div>
            {/* Only engagements that arrived through the request process have
                one, so the row is absent rather than empty on the rest. */}
            {eng.requested_by_name && (
              <div><strong>Requested By:</strong> {eng.requested_by_name}</div>
            )}
            {APPLICATION_TYPES.includes(eng.engagement_type) && (
              <div>
                <strong>Application:</strong>{' '}
                {eng.application_name ? (
                  <>
                    {eng.application_name}
                    {eng.application_tracking_id && (
                      <span style={{ color: 'var(--text-secondary)' }}> ({eng.application_tracking_id})</span>
                    )}
                  </>
                ) : canLinkApplication ? (
                  <span onClick={openLinkApplication}
                    style={{ color: 'var(--primary)', cursor: 'pointer', textDecoration: 'underline' }}
                    title="Link this engagement to an application">
                    Not linked — link one
                  </span>
                ) : (
                  <span style={{ color: 'var(--text-secondary)' }}>Not linked</span>
                )}
              </div>
            )}
            <div><strong>Start:</strong> {eng.start_date?.split('T')[0] || 'TBD'}</div>
            <div><strong>End:</strong> {eng.end_date?.split('T')[0] || 'TBD'}</div>
            <div><strong>CREST:</strong> {eng.crest_required ? '✅ Yes' : 'No'}</div>
            <div><strong>PCI:</strong> {eng.pci_required ? '✅ Yes' : 'No'}</div>
            <div><strong>Third Party:</strong> {eng.third_party ? '✅ Yes' : 'No'}</div>
            <div><strong>Client Obligation:</strong> {eng.client_obligation ? '✅ Yes' : 'No'}</div>
            {eng.client_obligation && (
              <div><strong>Client This Test is for:</strong> {eng.client_obligation_for || '—'}</div>
            )}
            <div><strong>Environment:</strong> {eng.environment_type || '—'}</div>
            {/* Beside Environment because the two together describe the
                conditions of the test. Both are unconditional rows, so this
                pairs cleanly whatever optional fields an engagement has. */}
            <div><strong>Approach:</strong> {eng.testing_approach || DEFAULT_APPROACH}</div>
            <div><strong>Tracking ID:</strong> {eng.customer_tracking_id || '—'}</div>
            {eng.service_order && <div><strong>Service Order:</strong> {eng.service_order}</div>}
            {eng.jira_project_key && <div><strong>Jira Project:</strong> {eng.jira_project_key}</div>}
            {isAdmin && eng.billing_amount > 0 && <div><strong>Billing:</strong> ${Number(eng.billing_amount).toLocaleString()}</div>}
            {eng.single_point_of_contact && <div><strong>Single Point of Contact:</strong> {eng.single_point_of_contact}</div>}
            {eng.technical_manager && <div><strong>Technical Manager:</strong> {eng.technical_manager}</div>}
            {isAdmin && eng.follow_up_date && <div><strong>Follow Up Date:</strong> <span style={{ color: new Date(eng.follow_up_date) <= new Date() ? '#eab308' : 'inherit', fontWeight: new Date(eng.follow_up_date) <= new Date() ? 700 : 400 }}>{new Date(eng.follow_up_date).toLocaleDateString()}</span></div>}
          </div>
          {eng.challenges_details && (
            <div style={{ marginTop: 12, padding: 12, borderRadius: 8, background: 'var(--bg-input)', fontSize: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>Challenges / Details</div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{eng.challenges_details}</div>
            </div>
          )}
          {['Application Penetration Test', 'API Penetration Test', 'Mobile Application Test'].includes(eng.engagement_type) && (
            <div style={{ marginTop: 16, border: '1px solid var(--border)', borderRadius: 8, padding: 14 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 10, color: 'var(--text-secondary)' }}>Application Scoping</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 14 }}>
                <div><strong>Company Owned:</strong> {eng.company_owned ? '✅ Yes' : 'No'}</div>
                <div><strong>Company Hosted:</strong> {eng.company_hosted ? '✅ Yes' : 'No'}</div>
                <div><strong>Internet Facing:</strong> {eng.internet_facing ? '✅ Yes' : 'No'}</div>
                <div><strong>Payment/Checkout:</strong> {eng.payment_function ? '✅ Yes' : 'No'}</div>
                <div><strong>File Upload:</strong> {eng.file_upload_function ? '✅ Yes' : 'No'}</div>
                <div><strong>IP Whitelist Needed:</strong> {eng.ip_whitelist_needed ? '✅ Yes' : 'No'}</div>
              </div>
            </div>
          )}
          <div style={{ marginTop: 16, border: '1px solid var(--border)', borderRadius: 8, padding: 14 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 10, color: 'var(--text-secondary)' }}>Testing Restrictions</h3>
            {(!eng.testing_restrictions || eng.testing_restrictions.noRestriction) ? (
              <div style={{ fontSize: 14 }}>No Restriction</div>
            ) : (
              <>
                <div style={{ fontSize: 14, marginBottom: 8 }}><strong>Time Zone:</strong> {eng.testing_restrictions.timeZone || '—'}</div>
                <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr 1fr', gap: 6, fontSize: 13 }}>
                  <div style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>Day</div>
                  <div style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>Restriction Start</div>
                  <div style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>Restriction End</div>
                  {DAYS.map(day => (
                    <React.Fragment key={day}>
                      <div style={{ fontWeight: 500 }}>{DAY_LABELS[day]}</div>
                      <div>{eng.testing_restrictions[day]?.start || '—'}</div>
                      <div>{eng.testing_restrictions[day]?.end || '—'}</div>
                    </React.Fragment>
                  ))}
                </div>
              </>
            )}
          </div>
          {eng.notes && (
            <div style={{ marginTop: 12, padding: '10px 14px', background: 'var(--bg-input)', borderRadius: 8, fontSize: 13, color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>
              <strong style={{ color: 'var(--text-primary)' }}>Notes:</strong> {eng.notes}
            </div>
          )}
        </Card>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {canEditBilling && (
          <Card>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: 'var(--text-secondary)' }}>Billing & SoW</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Statement of Work</label>
                <select
                  value={billingForm.sowId || ''}
                  onChange={e => { setBillingForm(f => ({ ...f, sowId: e.target.value })); setBillingDirty(true); }}
                  style={{ width: '100%', padding: '6px 8px', borderRadius: 'var(--radius)', fontSize: 13, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text)' }}>
                  <option value="">-- None --</option>
                  {billingSows.map(s => (
                    <option key={s.id} value={s.id}
                      disabled={s.assignable === false
                        ? s.id !== billingForm.sowId
                        : (s.remaining_credits <= 0 && s.id !== billingForm.sowId)}>
                      {s.assignable === false
                        ? `${s.sow_number} (${s.status})`
                        : `${s.sow_number} (${s.remaining_credits} credits remaining)`}
                    </option>
                  ))}
                </select>
              </div>
              {billingForm.sowId && (
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Credits (Qty)</label>
                  <select
                    value={parseFloat(billingForm.sowCredits) || 1}
                    onChange={e => { setBillingForm(f => ({ ...f, sowCredits: parseFloat(e.target.value) })); setBillingDirty(true); }}
                    style={{ width: '100%', padding: '6px 8px', borderRadius: 'var(--radius)', fontSize: 13, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text)' }}>
                    {[0.5,1,1.5,2,2.5,3,3.5,4,4.5,5,5.5,6,6.5,7,7.5,8,8.5,9,9.5,10].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              )}
              {billingForm.sowId && (() => {
                const sow = billingSows.find(s => s.id === billingForm.sowId);
                if (!sow) return null;
                const remaining = sow.remaining_credits;
                const total = sow.total_credits;
                return (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', justifyContent: 'space-between' }}>
                    <span>SoW: {sow.sow_number}</span>
                    <span style={{ fontWeight: 600, color: remaining <= 0 ? '#ef4444' : remaining <= 2 ? '#eab308' : '#22c55e' }}>{remaining}/{total} remaining</span>
                  </div>
                );
              })()}
              {eng.billing_amount > 0 && (
                <div style={{ fontSize: 13 }}>
                  <strong>Billing Amount:</strong> ${Number(eng.billing_amount).toLocaleString()}
                </div>
              )}
              {billingDirty && (
                <Button onClick={saveBilling} disabled={billingSaving} style={{ fontSize: 12, padding: '6px 14px' }}>
                  {billingSaving ? 'Saving...' : 'Save'}
                </Button>
              )}
            </div>
          </Card>
        )}
        <Card>
          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Actions</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {canEdit && (
              <Select label="Status" options={STATUSES.map(s => ({ value: s, label: s }))}
                value={eng.status || 'New'} onChange={e => updateEngagement({ status: e.target.value })} />
            )}
            {canAssignTester && (
              <Select label="Assign Tester" options={[{ value: '', label: 'Unassigned' }, ...testers.map(t => ({ value: t.id, label: t.full_name || t.username }))]}
                value={eng.tester_id || ''} onChange={e => updateEngagement({ testerId: e.target.value || null })} />
            )}
            {canEdit && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>Start Date</label>
                  <input type="date" value={eng.start_date?.split('T')[0] || ''}
                    onChange={e => updateEngagement({ startDate: e.target.value || null })}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: 13, background: 'var(--bg-input)', color: 'var(--text)', fontFamily: 'inherit' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>End Date</label>
                  <input type="date" value={eng.end_date?.split('T')[0] || ''}
                    onChange={e => updateEngagement({ endDate: e.target.value || null })}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: 13, background: 'var(--bg-input)', color: 'var(--text)', fontFamily: 'inherit' }} />
                </div>
              </div>
            )}
            {canReport && (
              <Button variant="success" disabled={!canGenerateReport}
                title={reportBlockedReason || 'Generate a report for this engagement'}
                onClick={() => canGenerateReport && openReportModal()}>
                Generate Report
              </Button>
            )}
          </div>
        </Card>
        </div>
      </div>
      )}

      {/* Tab: Notes */}
      {activeTab === 'notes' && (
      <>
      {/* Engagement Notes */}
      <Card style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Notes ({engNotes.length})</h2>
        {!isReadOnlyViewer && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <textarea
            value={newNote}
            onChange={e => setNewNote(e.target.value)}
            placeholder="Add a note..."
            style={{
              flex: 1, padding: '8px 12px', fontSize: 14, border: '1px solid var(--border)',
              borderRadius: 'var(--radius)', background: 'var(--bg-input)', color: 'var(--text)',
              fontFamily: 'inherit', resize: 'vertical', minHeight: 40
            }}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addEngNote(); } }}
          />
          <Button onClick={addEngNote} disabled={!newNote.trim()} style={{ alignSelf: 'flex-end' }}>Add Note</Button>
        </div>
        )}
        {engNotes.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {engNotes.map(note => (
              <div key={note.id} style={{ padding: '10px 14px', background: 'var(--bg-input)', borderRadius: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    <strong style={{ color: 'var(--text)' }}>{note.author_name || note.author_username}</strong>
                    {' — '}
                    {new Date(note.created_at).toLocaleString()}
                  </div>
                  {(note.created_by === user?.id || hasGroup('Super Admins') || hasGroup('Engagement Admins')) && (
                    <button onClick={() => deleteEngNote(note.id)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 14, padding: '0 4px' }}
                      title="Delete note">×</button>
                  )}
                </div>
                <div style={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>{note.content}</div>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>No notes yet.</p>
        )}
      </Card>
      </>
      )}

      {/* Tab: Documents */}
      {activeTab === 'documents' && (
      <>
      {/* Engagement Documents - File Uploads */}
      <Card style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600 }}>Engagement Documents ({files.filter(f => f.category !== 'Report Draft' && f.category !== 'Final Report').length})</h2>
          {canEdit && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Select label="" options={FILE_CATEGORIES.map(c => ({ value: c, label: c }))}
                value={uploadCategory} onChange={e => setUploadCategory(e.target.value)}
                style={{ marginBottom: 0, minWidth: 160 }} />
              <label style={{ cursor: uploading ? 'wait' : 'pointer' }}>
                <span style={{
                  padding: '10px 20px', borderRadius: 'var(--radius)', border: 'none',
                  fontSize: 14, fontWeight: 500, display: 'inline-flex', alignItems: 'center',
                  background: 'var(--primary)', color: 'white',
                  opacity: uploading ? 0.5 : 1, cursor: uploading ? 'wait' : 'pointer',
                }}>
                  {uploading ? 'Uploading...' : 'Upload'}
                </span>
                <input type="file" multiple style={{ display: 'none' }} onChange={handleFileUpload} disabled={uploading} />
              </label>
            </div>
          )}
        </div>
        {(() => {
          const docFiles = files.filter(f => f.category !== 'Report Draft' && f.category !== 'Final Report');
          return docFiles.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {docFiles.map(f => (
              <div key={f.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '10px 14px', background: 'var(--bg-input)', borderRadius: 8
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: 20 }}>
                    {f.category === 'API Collection' ? '🔌' :
                     f.mime_type?.startsWith('image/') ? '🖼️' :
                     f.mime_type?.includes('pdf') ? '📄' :
                     f.mime_type?.includes('word') || f.mime_type?.includes('document') ? '📝' :
                     f.mime_type?.includes('sheet') || f.mime_type?.includes('excel') ? '📊' :
                     f.mime_type?.includes('zip') || f.mime_type?.includes('compressed') ? '📦' : '📎'}
                  </span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {f.original_name}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'flex', gap: 12 }}>
                      <span><Badge>{f.category}</Badge></span>
                      <span>{formatFileSize(f.file_size)}</span>
                      <span>{f.uploaded_by_name}</span>
                      <span>{new Date(f.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <Button variant="secondary" style={{ padding: '4px 12px', fontSize: 12 }}
                    onClick={() => downloadFile(f.id, f.original_name)}>⬇️ Download</Button>
                  {(isAdmin || f.uploaded_by === user?.id) && (
                    <Button variant="danger" style={{ padding: '4px 12px', fontSize: 12 }}
                      onClick={() => deleteFile(f.id)}>Delete</Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : <p style={{ color: 'var(--text-secondary)' }}>No files uploaded</p>;
        })()}
      </Card>
      </>
      )}

      {/* Tab: Scope */}
      {activeTab === 'scope' && (
      <>
      {/* Scope Section */}
      <Card style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600 }}>Scope Items ({eng.scope_items?.length || 0})</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {/* The target file. Offered whenever there is something a scanner
                could take, whatever the engagement type — an application test
                with an IP in scope has the same need. Disabled rather than
                hidden when there is nothing, so the absence is explained by
                the tooltip rather than by a missing button. */}
            {(() => {
              const targets = (eng.scope_items || []).filter(s =>
                ['ip address', 'cidr range'].includes(String(s.scope_type || '').toLowerCase()));
              return (
                <Button variant="secondary" onClick={exportTargets} disabled={!targets.length}
                  title={targets.length
                    ? `Download ${targets.length} address${targets.length === 1 ? '' : 'es'} and range(s), one per line, for nmap -iL and similar`
                    : 'No IP addresses or CIDR ranges in scope to export'}>
                  Export targets (.txt)
                </Button>
              );
            })()}
            {(canEdit || !!user?.clientId) && !isReadOnlyViewer && usesNetworkScope(eng.engagement_type) && (
              <Button variant="secondary" onClick={openNetworkPicker}>From Networks</Button>
            )}
            {(canEdit || !!user?.clientId) && !isReadOnlyViewer && <Button onClick={() => setShowScopeModal(true)}>+ Add Scope</Button>}
          </div>
        </div>
        {eng.scope_items?.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {eng.scope_items.map(s => (
              <div key={s.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '10px 14px', background: 'var(--bg-input)', borderRadius: 8
              }}>
                <div>
                  <Badge>{s.scope_type}</Badge>
                  <span style={{ marginLeft: 12, fontSize: 14 }}>{s.value}</span>
                  {s.description && <span style={{ marginLeft: 8, fontSize: 13, color: 'var(--text-secondary)' }}>— {s.description}</span>}
                </div>
                {canEdit && <Button variant="danger" style={{ padding: '4px 12px', fontSize: 12 }} onClick={() => deleteScope(s.id)}>Remove</Button>}
              </div>
            ))}
          </div>
        ) : <p style={{ color: 'var(--text-secondary)' }}>No scope items defined</p>}
      </Card>

      {/* Credentials Section — visible to all engagement users, editable by testers */}
      <Card style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600 }}>🔑 Credentials ({credentials.length})</h2>
          {canEditCredentials && <Button onClick={openAddCredential}>+ Add Credential</Button>}
        </div>
        {credentials.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {credentials.map(cred => (
              <div key={cred.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '10px 14px', background: 'var(--bg-input)', borderRadius: 8
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
                  <Badge style={{
                    background: cred.credential_type === 'basic_auth' ? '#3b82f6' :
                      cred.credential_type === 'bearer_token' ? '#8b5cf6' :
                      cred.credential_type === 'api_key' ? '#f59e0b' :
                      cred.credential_type === 'cookie' ? '#10b981' : '#ec4899',
                    color: '#fff', fontSize: 11
                  }}>
                    {cred.credential_type === 'basic_auth' ? 'Basic Auth' :
                     cred.credential_type === 'bearer_token' ? 'Bearer Token' :
                     cred.credential_type === 'api_key' ? 'API Key' :
                     cred.credential_type === 'cookie' ? 'Cookie' : 'Form Login'}
                  </Badge>
                  <span style={{ fontWeight: 500, fontSize: 14 }}>{cred.label}</span>
                  {cred.has_username && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, color: 'var(--text-secondary)' }}>
                      user: ••••
                      <button onClick={() => copyCredentialField(cred.id, 'user')}
                        style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 4, cursor: 'pointer', color: copiedCredId === `${cred.id}-user` ? '#22c55e' : 'var(--text-secondary)', fontSize: 11, padding: '1px 6px' }}>
                        {copiedCredId === `${cred.id}-user` ? '✓' : '📋'}
                      </button>
                    </span>
                  )}
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, color: 'var(--text-secondary)' }}>
                    pass: ••••••••
                    <button onClick={() => copyCredentialField(cred.id, 'pass')}
                      style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 4, cursor: 'pointer', color: copiedCredId === `${cred.id}-pass` ? '#22c55e' : 'var(--text-secondary)', fontSize: 11, padding: '1px 6px' }}>
                      {copiedCredId === `${cred.id}-pass` ? '✓' : '📋'}
                    </button>
                  </span>
                  {!cred.is_active && <Badge style={{ background: '#6b7280', color: '#fff', fontSize: 10 }}>Inactive</Badge>}
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <Button variant="secondary" style={{ padding: '4px 12px', fontSize: 12 }}
                    onClick={() => copyCredentialField(cred.id, 'both')}>
                    {copiedCredId === `${cred.id}-both` ? '✓ Copied' : '📋 Copy Both'}
                  </Button>
                  {canEditCredentials && (
                    <>
                      <Button variant="secondary" style={{ padding: '4px 12px', fontSize: 12 }}
                        onClick={() => openEditCredential(cred)}>Edit</Button>
                      <Button variant="danger" style={{ padding: '4px 12px', fontSize: 12 }}
                        onClick={() => deleteCredential(cred.id)}>Remove</Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
            No credentials configured.{canEditCredentials ? ' Add credentials to enable authenticated scanning.' : ''}
          </p>
        )}
      </Card>

      {/* Testing Progress Summary */}
      {false && playbookSummary && playbookSummary.total > 0 && (
        <Card style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Testing Progress</h3>
          <div style={{
            height: 8, background: 'var(--bg-input)', borderRadius: 4, marginBottom: 16, overflow: 'hidden'
          }}>
            <div style={{
              height: '100%', borderRadius: 4,
              width: `${playbookSummary.progress_percent}%`,
              background: playbookSummary.progress_percent === 100 ? '#22c55e' : 'var(--primary)',
              transition: 'width 0.3s'
            }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, textAlign: 'center', fontSize: 13 }}>
            <div><div style={{ fontSize: 20, fontWeight: 700 }}>{playbookSummary.total}</div><div style={{ color: 'var(--text-secondary)' }}>Total</div></div>
            <div><div style={{ fontSize: 20, fontWeight: 700, color: '#94a3b8' }}>{playbookSummary.open}</div><div style={{ color: 'var(--text-secondary)' }}>Open</div></div>
            <div><div style={{ fontSize: 20, fontWeight: 700, color: '#ef4444' }}>{playbookSummary.vulnerable}</div><div style={{ color: 'var(--text-secondary)' }}>Vulnerable</div></div>
            <div><div style={{ fontSize: 20, fontWeight: 700, color: '#22c55e' }}>{playbookSummary.not_vulnerable}</div><div style={{ color: 'var(--text-secondary)' }}>Not Vulnerable</div></div>
            <div><div style={{ fontSize: 20, fontWeight: 700, color: '#f59e0b' }}>{playbookSummary.flagged}</div><div style={{ color: 'var(--text-secondary)' }}>Flagged</div></div>
          </div>
          <div style={{ textAlign: 'center', marginTop: 8, fontSize: 13, color: 'var(--text-secondary)' }}>
            {playbookSummary.progress_percent}% Complete
          </div>
        </Card>
      )}

      {/* Automated Scanning Section */}
      {false && eng && (
        <ScanManager
          engagementId={eng.id}
          engagementType={eng.engagement_type}
          canEdit={canEdit}
        />
      )}
      </>
      )}

      {/* Tab: Findings */}
      {activeTab === 'findings' && (
      <>
      {/* Findings Section */}
      <Card style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600 }}>Findings ({eng.findings?.length || 0})</h2>
          {canEdit && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Button variant="secondary" style={{ background: '#f97316', color: '#fff', border: 'none' }}
                onClick={() => { setShowBurpModal(true); setBurpFile(null); setBurpResult(null); }}>
                Import Burp XML
              </Button>
              {hasActiveIntegrations && (
                <>
                  <Button variant="secondary" style={{ background: '#0052CC', color: '#fff', border: 'none' }}
                    onClick={syncAllFindings} disabled={bulkSyncing}>
                    {bulkSyncing ? '⏳ Syncing...' : '🎫 Export All to Tickets'}
                  </Button>
                  {findingTickets.length > 0 && (
                    <Button variant="secondary" style={{ fontSize: 13 }}
                      onClick={pullTicketStatuses} disabled={pulling}>
                      {pulling ? '⏳ Refreshing...' : '🔄 Refresh Ticket Status'}
                    </Button>
                  )}
                </>
              )}
              <Button onClick={() => { setEditFinding(null); setFindingForm({}); setEvidence([]); setShowFindingModal(true); }}>+ Add Finding</Button>
            </div>
          )}
        </div>
        {/* Severity at a glance, above the table. The same five colours the
            report uses (report-helpers SEVERITY_COLORS), so the screen and the
            document a client reads do not disagree about what Critical looks
            like. Counts come from the findings already loaded — no extra
            request, and no chance of the row and the table disagreeing. */}
        {(eng.findings || []).length > 0 && (() => {
          const SEV = [
            ['Critical', '#7d1d1d'],
            ['High', '#dc2626'],
            ['Medium', '#f97316'],
            ['Low', '#eab308'],
            ['Info', '#3b82f6'],
          ];
          const counts = (eng.findings || []).reduce((acc, f) => {
            const k = f.severity || 'Info';
            acc[k] = (acc[k] || 0) + 1;
            return acc;
          }, {});
          return (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
              gap: 12, marginBottom: 16,
            }}>
              {SEV.map(([label, colour]) => (
                <div key={label} style={{
                  background: colour, color: '#fff', borderRadius: 10,
                  padding: '14px 10px', textAlign: 'center',
                  // A zero is worth showing: "no Criticals" is a result, and a
                  // row that hides the empty severities makes the reader count
                  // which ones are missing.
                  opacity: counts[label] ? 1 : 0.45,
                }}>
                  <div style={{ fontSize: 28, fontWeight: 300, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
                    {counts[label] || 0}
                  </div>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: 4 }}>
                    {label}
                  </div>
                </div>
              ))}
            </div>
          );
        })()}

        <Table columns={findingCols} data={eng.findings || []} sortable
          onRowClick={row => { setViewFinding(row); loadEvidence(row.id); }} />
      </Card>

      {/* Testing Playbook */}
      {false && <Card style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600 }}>Testing Playbook ({playbookItems.length})</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            {canEdit && playbookItems.length > 0 && (
              <Button variant="secondary" style={{ fontSize: 12 }}
                onClick={() => {
                  const allExpanded = Object.values(expandedCategories).every(v => v);
                  const cats = {};
                  Object.keys(playbookByCategory).forEach(c => { cats[c] = !allExpanded; });
                  setExpandedCategories(cats);
                }}>
                {Object.values(expandedCategories).every(v => v) ? '▼ Collapse All' : '▶ Expand All'}
              </Button>
            )}
            {canEdit && playbookItems.length === 0 && (
              <Button onClick={initializePlaybook} disabled={playbookLoading}>
                {playbookLoading ? '⏳ Initializing...' : '📋 Initialize Playbook'}
              </Button>
            )}
          </div>
        </div>

        {playbookItems.length > 0 ? (
          <div>
            {Object.entries(playbookByCategory).map(([category, items]) => {
              const completed = items.filter(i => i.status !== 'Open').length;
              return (
                <div key={category} style={{ marginBottom: 8 }}>
                  {/* Category header */}
                  <div
                    onClick={() => setExpandedCategories(prev => ({ ...prev, [category]: !prev[category] }))}
                    style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '10px 14px', background: 'var(--bg-input)', borderRadius: 8,
                      cursor: 'pointer', marginBottom: expandedCategories[category] ? 2 : 0,
                      userSelect: 'none'
                    }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{expandedCategories[category] ? '▼' : '▶'}</span>
                      <strong style={{ fontSize: 14 }}>{category}</strong>
                      <Badge>{items.length} tests</Badge>
                    </div>
                    <span style={{ fontSize: 12, color: completed === items.length ? '#22c55e' : 'var(--text-secondary)', fontWeight: 600 }}>
                      {completed}/{items.length}
                    </span>
                  </div>

                  {/* Category items */}
                  {expandedCategories[category] && items.map(item => (
                    <div key={item.id} style={{
                      padding: '10px 14px 10px 36px', borderBottom: '1px solid var(--border)'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                          <span style={{
                            width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                            background: statusColors[item.status] || '#94a3b8'
                          }} />
                          <span style={{ fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.title}</span>
                          {item.reference_url && (
                            <a href={item.reference_url} target="_blank" rel="noopener noreferrer"
                              title={item.reference_url.includes('cheatsheetseries') ? 'OWASP Cheat Sheet' :
                                     item.reference_url.includes('attack.mitre.org') ? 'MITRE ATT&CK' :
                                     item.reference_url.includes('mas.owasp.org') ? 'OWASP MASVS/MASTG' :
                                     item.reference_url.includes('API-Security') ? 'OWASP API Security' : 'OWASP Reference'}
                              onClick={e => e.stopPropagation()}
                              style={{
                                flexShrink: 0, fontSize: 12, color: 'var(--accent)', textDecoration: 'none',
                                padding: '2px 6px', borderRadius: 4, background: 'var(--bg-input)',
                                border: '1px solid var(--border)', whiteSpace: 'nowrap'
                              }}>
                              {item.reference_url.includes('cheatsheetseries') ? '📖 Cheat Sheet' :
                               item.reference_url.includes('attack.mitre.org') ? '🎯 ATT&CK' :
                               item.reference_url.includes('mas.owasp.org') ? '📱 MASVS' :
                               item.reference_url.includes('API-Security') ? '🔌 API Top 10' : '📋 OWASP'}
                            </a>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                          {canEdit ? (
                            <select
                              value={item.status}
                              onChange={e => updatePlaybookItem(item.id, { status: e.target.value, notes: item.notes })}
                              style={{
                                padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border)',
                                background: 'var(--bg-input)', color: 'var(--text)', fontSize: 12,
                                cursor: 'pointer', minWidth: 140
                              }}>
                              <option value="Open">Open</option>
                              <option value="Vulnerable">Vulnerable</option>
                              <option value="Not Vulnerable">Not Vulnerable</option>
                              <option value="Flag for Follow Up">Flag for Follow Up</option>
                            </select>
                          ) : (
                            <Badge>{item.status}</Badge>
                          )}
                          {canEdit && (
                            <button onClick={() => deletePlaybookItem(item.id)} title="Remove test case"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--text-secondary)', padding: '2px 4px' }}>
                              ×
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Description */}
                      {item.description && (
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, paddingLeft: 18 }}>
                          {item.description}
                        </div>
                      )}

                      {/* Notes area */}
                      {(item.notes || editingNotes === item.id) && canEdit ? (
                        <div style={{ marginTop: 8, paddingLeft: 18 }}>
                          <textarea
                            placeholder="Add testing notes..."
                            defaultValue={item.notes || ''}
                            onBlur={e => {
                              updatePlaybookItem(item.id, { status: item.status, notes: e.target.value });
                              if (!e.target.value) setEditingNotes(null);
                            }}
                            style={{
                              width: '100%', minHeight: 60, padding: '8px 10px', fontSize: 13,
                              border: '1px solid var(--border)', borderRadius: 6,
                              background: 'var(--bg-input)', color: 'var(--text)',
                              fontFamily: 'inherit', resize: 'vertical'
                            }}
                          />
                        </div>
                      ) : item.notes && !canEdit ? (
                        <div style={{ marginTop: 8, paddingLeft: 18, fontSize: 13, color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>
                          {item.notes}
                        </div>
                      ) : null}

                      {/* Add notes toggle */}
                      {!item.notes && editingNotes !== item.id && canEdit && (
                        <button onClick={() => setEditingNotes(item.id)}
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            fontSize: 12, color: 'var(--text-secondary)', padding: '4px 0 0 18px'
                          }}>
                          + Add notes
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '32px 20px', color: 'var(--text-secondary)' }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>📋</div>
            <p style={{ fontSize: 14, marginBottom: 4 }}>No playbook initialized</p>
            {canEdit && <p style={{ fontSize: 13 }}>Click "Initialize Playbook" to load test cases for this engagement type.</p>}
          </div>
        )}
      </Card>}
      </>
      )}

      {/* Tab: Reports — the whole card is the drop target, so a dragged file
          can be let go anywhere over the reports list, not just on the button. */}
      {activeTab === 'reports' && (
        <Card
          onDragEnter={canReport ? (e => { e.preventDefault(); setReportDragDepth(d => d + 1); }) : undefined}
          onDragOver={canReport ? (e => e.preventDefault()) : undefined}
          onDragLeave={canReport ? (() => setReportDragDepth(d => Math.max(0, d - 1))) : undefined}
          onDrop={canReport ? handleReportDrop : undefined}
          style={canReport && reportDragDepth > 0
            ? { outline: '2px dashed var(--primary)', outlineOffset: -6, background: 'rgba(79,143,255,0.06)' }
            : undefined}
        >
          {canReport && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px 0', gap: 8 }}>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                {reportDragDepth > 0
                  ? 'Drop to upload'
                  : `Drag files here to upload — ${REPORT_UPLOAD_EXTENSIONS.join(', ')}`}
              </span>
              <label style={{ cursor: uploadingReport ? 'wait' : 'pointer' }}>
                <span style={{
                  padding: '6px 14px', borderRadius: 'var(--radius)', fontSize: 12, fontWeight: 500,
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: 'var(--bg-input)', color: 'var(--text-secondary)', border: '1px solid var(--border)',
                  cursor: uploadingReport ? 'wait' : 'pointer',
                }}>
                  {uploadingReport ? 'Uploading...' : '📎 Upload Report'}
                </span>
                <input type="file" multiple accept={REPORT_UPLOAD_EXTENSIONS.join(',')} style={{ display: 'none' }}
                  onChange={handleReportFileUpload} disabled={uploadingReport} />
              </label>
            </div>
          )}
          {reportUploadError && (
            <div style={{ margin: '12px 16px 0', padding: '10px 14px', borderRadius: 8, background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', fontSize: 13 }}>
              {reportUploadError}
            </div>
          )}
          {generatedReports.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
              <p style={{ fontSize: 14 }}>
                {reportBlockedReason
                  ? `No reports yet — ${reportBlockedReason.charAt(0).toLowerCase()}${reportBlockedReason.slice(1)}.`
                  : 'No reports yet. Use the "Generate Report" button in the header or upload a report file above.'}
              </p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-input)' }}>
                    {['File', 'Format', 'Version', 'Generated By', 'Date', 'Certificate', 'Deliveries', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-secondary)', letterSpacing: '0.05em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {generatedReports.map(r => (
                    <React.Fragment key={r.id}>
                      <tr style={{ borderTop: '1px solid var(--border)' }}>
                        <td style={{ padding: '10px 14px', fontSize: 13 }}>
                          <div style={{ fontWeight: 600 }}>{r.file_name}</div>
                          {r.content_hash && <div style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: 'monospace' }}>SHA-256: {r.content_hash.substring(0, 16)}...</div>}
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4, background: r.format === 'pdf' ? 'rgba(239,68,68,0.1)' : 'rgba(59,130,246,0.1)', color: r.format === 'pdf' ? '#ef4444' : '#3b82f6' }}>
                            {(r.format || 'docx').toUpperCase()}
                          </span>
                          {r.has_pdf_password && (
                            <span title="Password-protected" style={{ marginLeft: 4, fontSize: 11 }}>🔒</span>
                          )}
                        </td>
                        <td style={{ padding: '10px 14px', fontSize: 13 }}>
                          {editingReportVersion === r.id ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <input type="number" step="0.1" min="0.1" value={editReportVersionVal}
                                onChange={e => setEditReportVersionVal(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') saveReportVersion(r.id); if (e.key === 'Escape') setEditingReportVersion(null); }}
                                autoFocus
                                style={{ width: 60, padding: '2px 6px', fontSize: 12, fontWeight: 600, border: '1px solid var(--primary)', borderRadius: 4, background: 'var(--bg-input)', color: 'var(--text)', fontFamily: 'inherit' }} />
                              <button onClick={() => saveReportVersion(r.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#22c55e', fontSize: 14, padding: 0 }}>✓</button>
                              <button onClick={() => setEditingReportVersion(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 14, padding: 0 }}>✕</button>
                            </div>
                          ) : (
                            <span style={{ fontWeight: 600, cursor: canReport ? 'pointer' : 'default' }}
                              onClick={() => { if (canReport) { setEditingReportVersion(r.id); setEditReportVersionVal(String(parseFloat(r.version) || 1)); } }}
                              title={canReport ? 'Click to edit version' : ''}>
                              v{parseFloat(r.version) || 1}
                            </span>
                          )}
                          {r.is_current && editingReportVersion !== r.id && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 600, padding: '1px 6px', background: '#22c55e', color: '#fff', borderRadius: 8 }}>CURRENT</span>}
                          {/* Versions are counted per template now, so two
                              rows can legitimately both read v1 CURRENT. The
                              template name is what tells them apart. */}
                          {r.template_name && (
                            <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{r.template_name}</div>
                          )}
                        </td>
                        <td style={{ padding: '10px 14px', fontSize: 13 }}>{r.generated_by_name || '-'}</td>
                        <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text-secondary)' }}>
                          {r.created_at ? new Date(r.created_at).toLocaleDateString() : '-'}
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          {r.cert_prefix ? (
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <span style={{ fontSize: 14 }}>🏅</span>
                                <span style={{ fontSize: 11, fontWeight: 600, color: r.cert_revoked_at ? '#f59e0b' : '#22c55e' }}>
                                  {r.cert_revoked_at ? 'Revoked' : 'Certified'}
                                </span>
                              </div>
                              {!r.cert_revoked_at && r.cert_verification_count > 0 && (
                                <div style={{ fontSize: 10, color: 'var(--text-muted)', marginLeft: 18 }}>Verified {r.cert_verification_count}x</div>
                              )}
                            </div>
                          ) : (
                            <span style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                              <span style={{ fontSize: 12, opacity: 0.5 }}>○</span> Not Certified
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          <button onClick={() => { if (!reportDeliveries[r.id]) loadDeliveries(r.id); else setReportDeliveries(prev => { const n = { ...prev }; delete n[r.id]; return n; }); }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--primary)', fontFamily: 'inherit' }}>
                            {r.delivery_count || 0} {r.delivery_count === 1 ? 'delivery' : 'deliveries'}
                          </button>
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            <Button variant="primary" style={{ padding: '3px 10px', fontSize: 11 }} onClick={() => downloadReport(r.id, r.file_name)}>Download</Button>
                            {canReport && <Button variant="secondary" style={{ padding: '3px 10px', fontSize: 11 }} onClick={() => setShowDeliveryModal(r.id)}>Record Delivery</Button>}
                            {isMspPenTester && gotenbergEnabled && (r.format || 'docx') === 'docx' && !r.protected_pdf_id && (
                              <Button variant="secondary" style={{ padding: '3px 10px', fontSize: 11 }}
                                disabled={protectingReport === r.id}
                                onClick={() => protectReport(r.id, r.file_name)}>
                                {protectingReport === r.id ? 'Protecting…' : 'Protect PDF'}
                              </Button>
                            )}
                            {isMspPenTester && (r.format || 'docx') === 'docx' && r.protected_pdf_id && (
                              // Already protected. Offering the conversion again would only
                              // ever 409, so the row points at the password instead -- which
                              // is what someone looking at this version actually wants.
                              <Button variant="secondary" style={{ padding: '3px 10px', fontSize: 11 }}
                                onClick={() => {
                                  const target = generatedReports.find(x => x.id === r.protected_pdf_id);
                                  openPasswordModal(target || { id: r.protected_pdf_id, file_name: r.file_name });
                                }}>
                                🔑 PDF password
                              </Button>
                            )}
                            {r.password_visible && (
                              <Button variant="secondary" style={{ padding: '3px 10px', fontSize: 11 }}
                                onClick={() => openPasswordModal(r)}>
                                🔑 Password{isMspPenTester && r.password_share_count > 0 ? ` (${r.password_share_count})` : ''}
                              </Button>
                            )}
                            {canReport && r.cert_prefix && !r.cert_revoked_at && (
                              <Button variant="danger" style={{ padding: '3px 10px', fontSize: 11 }} onClick={() => setShowRevokeModal(r.id)}>Revoke Cert</Button>
                            )}
                            {(hasGroup('Penetration Testers') || hasGroup('Super Admins')) && !r.is_current && (
                              <Button variant="secondary" style={{ padding: '3px 10px', fontSize: 11 }}
                                onClick={() => markReportCurrent(r.id, r.file_name)}>Make Current</Button>
                            )}
                            {hasGroup('Super Admins') && (
                              <Button variant="danger" style={{ padding: '3px 10px', fontSize: 11 }} onClick={() => deleteReport(r.id, r.file_name)}>Delete</Button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {reportDeliveries[r.id] && (
                        <tr><td colSpan={8} style={{ padding: '0 14px 10px 28px', background: 'var(--bg)' }}>
                          {reportDeliveries[r.id].length === 0 ? (
                            <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '8px 0' }}>No deliveries recorded.</div>
                          ) : (
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                              <thead><tr>
                                {['Method', 'Recipient', 'Delivered By', 'Date', 'Notes'].map(h => (
                                  <th key={h} style={{ padding: '6px 10px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>{h}</th>
                                ))}
                              </tr></thead>
                              <tbody>
                                {reportDeliveries[r.id].map(d => (
                                  <tr key={d.id} style={{ borderTop: '1px solid var(--border)' }}>
                                    <td style={{ padding: '6px 10px', textTransform: 'capitalize' }}>{d.delivery_method}</td>
                                    <td style={{ padding: '6px 10px' }}>{d.recipient_name || '-'}{d.recipient_email ? ` (${d.recipient_email})` : ''}</td>
                                    <td style={{ padding: '6px 10px' }}>{d.delivered_by_name || '-'}</td>
                                    <td style={{ padding: '6px 10px', color: 'var(--text-secondary)' }}>{d.delivered_at ? new Date(d.delivered_at).toLocaleDateString() : '-'}</td>
                                    <td style={{ padding: '6px 10px', color: 'var(--text-secondary)' }}>{d.notes || '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </td></tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Delivery Modal */}
      <Modal isOpen={!!passwordModal} onClose={() => { setPasswordModal(null); setPasswordData(null); setPasswordShares(null); }}
        title="Report PDF password" width={560}>
        {passwordModal && (
          <div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12, wordBreak: 'break-all' }}>
              {passwordModal.file_name}
            </div>

            {passwordBusy && !passwordData && (
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading…</div>
            )}

            {passwordData && (
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-secondary)', letterSpacing: '0.05em', marginBottom: 6 }}>
                  Password to open the file
                </label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <code style={{ flex: 1, padding: '10px 12px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 15, letterSpacing: '0.04em', userSelect: 'all' }}>
                    {passwordData.userPassword}
                  </code>
                  <Button variant="secondary" style={{ padding: '6px 12px', fontSize: 12 }}
                    onClick={() => navigator.clipboard?.writeText(passwordData.userPassword)}>Copy</Button>
                </div>
                {passwordData.ownerPassword && (
                  <div style={{ marginTop: 10, fontSize: 11, color: 'var(--text-muted)' }}>
                    Owner password (never share): <code style={{ userSelect: 'all' }}>{passwordData.ownerPassword}</code>
                  </div>
                )}
                <div style={{ marginTop: 10, fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                  Every time this password is viewed it is recorded against the engagement.
                  Encryption keeps out anyone without the password; it cannot stop someone
                  who has it from saving an unprotected copy.
                </div>
              </div>
            )}

            {isMspPenTester && passwordShares && (
              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-secondary)', letterSpacing: '0.05em', marginBottom: 8 }}>
                  Shared with
                </label>

                {passwordShares.shares.filter(x => !x.revoked_at).length === 0 && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
                    Nobody yet. Only MSP Penetration Testers can see this password.
                  </div>
                )}

                {passwordShares.shares.filter(x => !x.revoked_at).map(sh => (
                  <div key={sh.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '6px 0', fontSize: 13 }}>
                    <div>
                      <div style={{ fontWeight: 600 }}>{sh.full_name || sh.username}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                        {sh.email}{sh.shared_by_name ? ` · shared by ${sh.shared_by_name}` : ''}
                      </div>
                    </div>
                    <Button variant="danger" style={{ padding: '3px 10px', fontSize: 11 }} disabled={passwordBusy}
                      onClick={() => revokePasswordShare(sh.user_id, sh.full_name || sh.username)}>Revoke</Button>
                  </div>
                ))}

                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <select value={shareUserId} onChange={e => setShareUserId(e.target.value)}
                    style={{ flex: 1, padding: '8px 10px', fontSize: 13, background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontFamily: 'inherit' }}>
                    <option value="">Share with a user at this client…</option>
                    {passwordShares.candidates
                      .filter(c => !passwordShares.shares.some(sh => sh.user_id === c.id && !sh.revoked_at))
                      .map(c => (
                        <option key={c.id} value={c.id}>{c.full_name || c.username} ({c.email})</option>
                      ))}
                  </select>
                  <Button disabled={!shareUserId || passwordBusy} onClick={sharePassword}>Share</Button>
                </div>
                <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                  Only users belonging to this engagement's client can be chosen. A share
                  lasts until it is revoked, and stops working on its own if the person
                  loses access to the engagement.
                </div>

                {passwordShares.shares.some(x => x.revoked_at) && (
                  <details style={{ marginTop: 12 }}>
                    <summary style={{ fontSize: 11, color: 'var(--text-muted)', cursor: 'pointer' }}>
                      Previously shared with ({passwordShares.shares.filter(x => x.revoked_at).length})
                    </summary>
                    {passwordShares.shares.filter(x => x.revoked_at).map(sh => (
                      <div key={sh.id} style={{ fontSize: 12, color: 'var(--text-muted)', padding: '4px 0' }}>
                        {sh.full_name || sh.username} — revoked {new Date(sh.revoked_at).toLocaleDateString()}
                      </div>
                    ))}
                  </details>
                )}
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal isOpen={!!showDeliveryModal} onClose={() => setShowDeliveryModal(null)} title="Record Report Delivery" width={440}>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>Delivery Method *</label>
          <select value={deliveryForm.deliveryMethod} onChange={e => setDeliveryForm(f => ({ ...f, deliveryMethod: e.target.value }))}
            style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: 13, background: 'var(--bg-input)', color: 'var(--text)', fontFamily: 'inherit' }}>
            <option value="email">Email</option>
            <option value="portal">Portal / App</option>
            <option value="in-person">In-Person</option>
            <option value="secure-share">Secure Share</option>
            <option value="other">Other</option>
          </select>
        </div>
        <Input label="Recipient Name" value={deliveryForm.recipientName} onChange={e => setDeliveryForm(f => ({ ...f, recipientName: e.target.value }))} />
        <Input label="Recipient Email" value={deliveryForm.recipientEmail} onChange={e => setDeliveryForm(f => ({ ...f, recipientEmail: e.target.value }))} />
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>Notes</label>
          <textarea value={deliveryForm.notes} onChange={e => setDeliveryForm(f => ({ ...f, notes: e.target.value }))} rows={2}
            placeholder="Optional..." style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: 13, background: 'var(--bg-input)', color: 'var(--text)', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={() => setShowDeliveryModal(null)}>Cancel</Button>
          <Button onClick={recordDelivery}>Record Delivery</Button>
        </div>
      </Modal>

      {/* Revoke Certificate Modal */}
      <Modal isOpen={!!showRevokeModal} onClose={() => setShowRevokeModal(null)} title="Revoke Certificate" width={400}>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
          This action is irreversible. The certificate will show as revoked on the public verification page.
          To issue a new certificate, generate a new version of the report.
        </p>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>Reason (optional)</label>
          <textarea value={revokeReason} onChange={e => setRevokeReason(e.target.value)} rows={2}
            placeholder="Why is this certificate being revoked?"
            style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: 13, background: 'var(--bg-input)', color: 'var(--text)', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={() => setShowRevokeModal(null)}>Cancel</Button>
          <Button variant="danger" onClick={revokeCertificate}>Revoke Certificate</Button>
        </div>
      </Modal>

      {/* Tab: Activity */}
      {activeTab === 'activity' && (
        <Card>
          {activityLog.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
              <p style={{ fontSize: 14 }}>No activity recorded yet.</p>
            </div>
          ) : (
            <div style={{ padding: '8px 0' }}>
              {activityLog.map((log, i) => {
                const actionColors = {
                  CREATE: '#22c55e', UPDATE: '#f59e0b', DELETE: '#ef4444',
                  LOGIN: '#3b82f6', GENERATE: '#8b5cf6', DELIVER: '#06b6d4',
                  DOWNLOAD: '#0ea5e9',
                  REVOKE: '#ef4444', IMPORT: '#3b82f6', ASSIGN: '#8b5cf6',
                };
                const color = Object.entries(actionColors).find(([k]) => log.action?.startsWith(k))?.[1] || '#6b7280';
                const actionLabel = (log.action || '').split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');
                const details = typeof log.details === 'string' ? (() => { try { return JSON.parse(log.details); } catch { return null; } })() : log.details;

                let description = '';
                if (details?.summary) description = details.summary;
                else if (details?.new_status && details?.old_status) description = `Status: ${details.old_status} → ${details.new_status}`;
                else if (details?.status && details?.old_status) description = `Status: ${details.old_status} → ${details.status}`;
                else if (details?.groups) description = `Groups: ${details.groups.join(', ')}`;
                else if (details?.deliveryMethod) description = `Delivered via ${details.deliveryMethod}${details.recipientName ? ` to ${details.recipientName}` : ''}`;
                else if (details?.format) description = `Format: ${details.format.toUpperCase()}${details.version ? ` (v${details.version})` : ''}`;
                else if (details?.certPrefix) description = `Certificate: ${details.certPrefix}...`;
                else if (details?.isBilled !== undefined) description = details.isBilled ? 'Marked as billed' : 'Marked as unbilled';
                else if (details?.sowId) description = `SoW assigned (${details.sowCredits || 1} credits)`;
                else if (details?.testerId) description = details.testerId ? 'Tester assigned' : 'Tester unassigned';
                else if (details?.startDate !== undefined || details?.endDate !== undefined) description = [details.startDate && `Start: ${details.startDate}`, details.endDate && `End: ${details.endDate}`].filter(Boolean).join(', ');

                return (
                  <div key={log.id} style={{ display: 'flex', gap: 12, padding: '12px 16px', borderBottom: i < activityLog.length - 1 ? '1px solid var(--border)' : 'none' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: color, marginTop: 6 }} />
                      {i < activityLog.length - 1 && <div style={{ width: 1, flex: 1, background: 'var(--border)', marginTop: 4 }} />}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '1px 8px', borderRadius: 10, background: color + '18', color, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                          {actionLabel}
                        </span>
                        <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                          {new Date(log.created_at).toLocaleDateString()}{' '}
                          {new Date(log.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <div style={{ fontSize: 13, color: 'var(--text)' }}>
                        <strong>{log.actor_label || log.full_name || log.username || 'System'}</strong>
                        {description && <span style={{ color: 'var(--text-secondary)' }}> — {description}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      {/* Status Modal */}
      <Modal isOpen={showStatusModal} onClose={() => setShowStatusModal(false)} title="Update Status" width={400}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {STATUSES.map(s => {
            const isTechMgr = hasGroup('Technical Managers') || hasGroup('Super Admins');
            const disableComplete = s === 'Complete' && eng.status === 'In Review' && !isTechMgr;
            const disableCancelled = s === 'Cancelled' && !isTechMgr;
            const disableNew = s === 'New' && !isAdmin;
            // Mirrors the server rules in backend/routes/engagements.js: a
            // revalidation re-tests a report that was already delivered, and
            // ending one freezes each finding's outcome into that report.
            const disableRetest = s === 'In Retest' && s !== eng.status
              && !['Complete', 'Retest Complete'].includes(eng.status);
            const disableRetestDone = s === 'Retest Complete' && !isTechMgr;
            // These statuses assert the testing happened, so the dates it
            // happened on have to be recorded first — compliance reporting
            // reads them. The server enforces this; blocking the button here
            // just says so before the click rather than after.
            const missingDates = (s === 'In Review' || s === 'Complete') && s !== eng.status &&
              [!eng.start_date && 'start date', !eng.end_date && 'end date'].filter(Boolean);
            const needsDates = missingDates && missingDates.length > 0;
            return (
            <Button key={s} variant={eng.status === s ? 'primary' : 'secondary'}
              onClick={() => { updateEngagement({ status: s }); setShowStatusModal(false); }}
              disabled={disableNew || disableComplete || disableCancelled || disableRetest || disableRetestDone || needsDates}>
              {s} {eng.status === s ? '(current)' : ''}
              {(disableComplete || disableCancelled || disableRetestDone) && <span style={{ fontSize: 10, color: 'var(--text-secondary)', marginLeft: 4 }}>(Technical Manager)</span>}
              {disableRetest && <span style={{ fontSize: 10, color: 'var(--text-secondary)', marginLeft: 4 }}>(from Complete only)</span>}
              {needsDates && <span style={{ fontSize: 10, color: 'var(--text-secondary)', marginLeft: 4 }}>(needs {missingDates.join(' and ')})</span>}
            </Button>
            );
          })}
        </div>
      </Modal>

      {/* Edit Engagement Modal */}
      <Modal isOpen={showEditEngModal} onClose={() => setShowEditEngModal(false)} title="Edit Engagement Details" width={700} noOverlayClose>
        <Input label="Engagement Name" value={engEditForm.name || ''} placeholder="Optional name for this engagement"
          onChange={e => setEngEditForm(f => ({ ...f, name: e.target.value }))} />
        <Select label="Engagement Type *"
          options={ENGAGEMENT_TYPES.map(t => ({ value: t, label: t }))}
          value={engEditForm.engagementType || ''}
          onChange={e => setEngEditForm(f => ({ ...f, engagementType: e.target.value }))} />
        <Select label="Environment Type"
          options={[{ value: '', label: 'Select Environment' }, ...ENV_TYPES.map(t => ({ value: t, label: t }))]}
          value={engEditForm.environmentType || ''}
          onChange={e => setEngEditForm(f => ({ ...f, environmentType: e.target.value }))} />
        <Select label="Approach"
          options={APPROACHES.map(t => ({ value: t, label: t }))}
          value={engEditForm.testingApproach || DEFAULT_APPROACH}
          onChange={e => setEngEditForm(f => ({ ...f, testingApproach: e.target.value }))} />
        {canAssignTester && (
          <Select label="Assigned Tester"
            options={[{ value: '', label: 'Unassigned' }, ...testers.map(t => ({ value: t.id, label: t.full_name || t.username }))]}
            value={engEditForm.testerId || ''}
            onChange={e => setEngEditForm(f => ({ ...f, testerId: e.target.value }))} />
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Input label="Start Date" type="date" value={engEditForm.startDate || ''}
            onChange={e => setEngEditForm(f => ({ ...f, startDate: e.target.value }))} />
          <Input label="End Date" type="date" value={engEditForm.endDate || ''}
            onChange={e => setEngEditForm(f => ({ ...f, endDate: e.target.value }))} />
        </div>
        {editSows.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16 }}>
            <Select label="Statement of Work (optional)"
              options={[{ value: '', label: '— None —' }, ...editSows.map(s => ({
                value: s.id,
                // A closed SoW appears here only because this engagement is on
                // it; "(Complete)" says why it is in the list.
                label: s.assignable === false
                  ? `${s.sow_number} (${s.status})`
                  : `${s.sow_number} (${s.remaining_credits} credits remaining)`
              }))]}
              value={engEditForm.sowId || ''}
              onChange={e => setEngEditForm(f => ({ ...f, sowId: e.target.value }))} />
            {engEditForm.sowId && (
              <Select label="Credits (Qty)"
                options={[0.5,1,1.5,2,2.5,3,3.5,4,4.5,5,5.5,6,6.5,7,7.5,8,8.5,9,9.5,10].map(n => ({ value: n, label: `${n}` }))}
                value={parseFloat(engEditForm.sowCredits) || 1}
                onChange={e => setEngEditForm(f => ({ ...f, sowCredits: parseFloat(e.target.value) }))} />
            )}
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
              <input type="checkbox" checked={engEditForm.crestRequired || false}
                onChange={e => setEngEditForm(f => ({ ...f, crestRequired: e.target.checked }))} />
              CREST Required
            </label>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
              <input type="checkbox" checked={engEditForm.pciRequired || false}
                onChange={e => setEngEditForm(f => ({ ...f, pciRequired: e.target.checked }))} />
              PCI Required
            </label>
          </div>
          <Select label="Third Party" options={[{ value: 'false', label: 'No' }, { value: 'true', label: 'Yes' }]}
            value={engEditForm.thirdParty ? 'true' : 'false'}
            onChange={e => setEngEditForm(f => ({ ...f, thirdParty: e.target.value === 'true' }))} />
        </div>
        {engEditForm.thirdParty && editVendors.length > 0 && (
          <Select label="Vendor" value={engEditForm.vendorId || (editVendors.find(v => v.is_msp_default)?.id || '')}
            onChange={e => setEngEditForm(f => ({ ...f, vendorId: e.target.value }))}
            options={editVendors.map(v => ({ value: v.id, label: v.name + (v.is_msp_default ? ' (MSP)' : '') }))} />
        )}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
            <input type="checkbox" checked={engEditForm.clientObligation || false}
              onChange={e => setEngEditForm(f => ({ ...f, clientObligation: e.target.checked }))} />
            Client Obligation
          </label>
          {engEditForm.clientObligation && (
            <Input label="Client This Test is for" style={{ marginTop: 12, marginBottom: 0 }}
              value={engEditForm.clientObligationFor || ''}
              onChange={e => setEngEditForm(f => ({ ...f, clientObligationFor: e.target.value }))}
              placeholder="e.g. the customer or partner requiring this test" />
          )}
        </div>
        {['Application Penetration Test', 'API Penetration Test', 'Mobile Application Test'].includes(engEditForm.engagementType) && (
          <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 16, marginBottom: 16 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: 'var(--text-secondary)' }}>Application Scoping Questions</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <Select label="Application Owned by Company?" options={[{ value: 'false', label: 'No' }, { value: 'true', label: 'Yes' }]}
                value={engEditForm.companyOwned || 'false'} onChange={e => setEngEditForm(f => ({ ...f, companyOwned: e.target.value }))} />
              <Select label="Application Hosted by Company?" options={[{ value: 'false', label: 'No' }, { value: 'true', label: 'Yes' }]}
                value={engEditForm.companyHosted || 'false'} onChange={e => setEngEditForm(f => ({ ...f, companyHosted: e.target.value }))} />
              <Select label="Internet Facing?" options={[{ value: 'false', label: 'No' }, { value: 'true', label: 'Yes' }]}
                value={engEditForm.internetFacing || 'false'} onChange={e => setEngEditForm(f => ({ ...f, internetFacing: e.target.value }))} />
              <Select label="Payment/Checkout Functionality?" options={[{ value: 'false', label: 'No' }, { value: 'true', label: 'Yes' }]}
                value={engEditForm.paymentFunction || 'false'} onChange={e => setEngEditForm(f => ({ ...f, paymentFunction: e.target.value }))} />
              <Select label="File Upload Functionality?" options={[{ value: 'false', label: 'No' }, { value: 'true', label: 'Yes' }]}
                value={engEditForm.fileUploadFunction || 'false'} onChange={e => setEngEditForm(f => ({ ...f, fileUploadFunction: e.target.value }))} />
              <Select label="IP Whitelist Needed?" options={[{ value: 'false', label: 'No' }, { value: 'true', label: 'Yes' }]}
                value={engEditForm.ipWhitelistNeeded || 'false'} onChange={e => setEngEditForm(f => ({ ...f, ipWhitelistNeeded: e.target.value }))} />
            </div>
          </div>
        )}
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: engEditForm.testingRestrictions?.noRestriction ? 0 : 12 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)', margin: 0 }}>Testing Restrictions</h3>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
              <input type="checkbox" checked={engEditForm.testingRestrictions?.noRestriction ?? true}
                onChange={e => setEngEditForm(f => ({ ...f, testingRestrictions: { ...(f.testingRestrictions || DEFAULT_RESTRICTIONS), noRestriction: e.target.checked } }))} />
              No Restriction
            </label>
          </div>
          {!engEditForm.testingRestrictions?.noRestriction && (
            <>
              <Select label="Time Zone" options={TIME_ZONES}
                value={engEditForm.testingRestrictions?.timeZone || ''} onChange={e => setEngEditForm(f => ({ ...f, testingRestrictions: { ...f.testingRestrictions, timeZone: e.target.value } }))} />
              <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr 1fr', gap: 8, alignItems: 'center', fontSize: 13 }}>
                <div style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>Day</div>
                <div style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>Restriction Start</div>
                <div style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>Restriction End</div>
                {DAYS.map(day => (
                  <React.Fragment key={day}>
                    <div style={{ fontWeight: 500 }}>{DAY_LABELS[day]}</div>
                    <input type="time" value={engEditForm.testingRestrictions?.[day]?.start || ''} style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text)', fontSize: 13 }}
                      onChange={e => setEngEditForm(f => ({ ...f, testingRestrictions: { ...f.testingRestrictions, [day]: { ...(f.testingRestrictions?.[day] || {}), start: e.target.value } } }))} />
                    <input type="time" value={engEditForm.testingRestrictions?.[day]?.end || ''} style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text)', fontSize: 13 }}
                      onChange={e => setEngEditForm(f => ({ ...f, testingRestrictions: { ...f.testingRestrictions, [day]: { ...(f.testingRestrictions?.[day] || {}), end: e.target.value } } }))} />
                  </React.Fragment>
                ))}
              </div>
            </>
          )}
        </div>
        <Input label="Customer Tracking ID" value={engEditForm.customerTrackingId || ''} placeholder="Client's internal reference number"
          onChange={e => setEngEditForm(f => ({ ...f, customerTrackingId: e.target.value }))} />
        <Input label="Service Order" value={engEditForm.serviceOrder || ''} placeholder="Service order or PO number"
          onChange={e => setEngEditForm(f => ({ ...f, serviceOrder: e.target.value }))} />
        <Input label="Jira Project Key" value={engEditForm.jiraProjectKey || ''} placeholder="e.g. SEC — leave blank for default"
          onChange={e => setEngEditForm(f => ({ ...f, jiraProjectKey: e.target.value }))} />
        {isAdmin && (
          <Input label="Billing Amount" type="number" min="0" step="0.01" value={engEditForm.billingAmount || ''}
            placeholder="0.00" onChange={e => setEngEditForm(f => ({ ...f, billingAmount: e.target.value }))} />
        )}
        <Input label="Single Point of Contact" value={engEditForm.singlePointOfContact || ''} placeholder="Name and contact info for primary POC"
          onChange={e => setEngEditForm(f => ({ ...f, singlePointOfContact: e.target.value }))} />
        <Input label="Technical Manager" value={engEditForm.technicalManager || ''} placeholder="Auto-populated when marked Complete"
          onChange={e => setEngEditForm(f => ({ ...f, technicalManager: e.target.value }))} />
        {isAdmin && (
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <Input label="Follow Up Date" type="date" value={engEditForm.followUpDate || ''}
                onChange={e => setEngEditForm(f => ({ ...f, followUpDate: e.target.value }))} />
            </div>
            {engEditForm.followUpDate && (
              <button onClick={() => setEngEditForm(f => ({ ...f, followUpDate: '' }))}
                style={{ padding: '8px 12px', marginBottom: 16, border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg-input)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 12 }}>
                Clear
              </button>
            )}
          </div>
        )}
        <TextArea label="Challenges / Details" value={engEditForm.challengesDetails || ''} placeholder="Any challenges, blockers, or additional details about this engagement"
          onChange={e => setEngEditForm(f => ({ ...f, challengesDetails: e.target.value }))} />
        <TextArea label="Notes" value={engEditForm.notes || ''} placeholder="Internal notes about this engagement"
          onChange={e => setEngEditForm(f => ({ ...f, notes: e.target.value }))} />
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
          <Button variant="secondary" onClick={() => setShowEditEngModal(false)}>Cancel</Button>
          <Button onClick={async () => {
            const isAppType = ['Application Penetration Test', 'API Penetration Test', 'Mobile Application Test'].includes(engEditForm.engagementType);
            await updateEngagement({
              name: engEditForm.name || null,
              engagementType: engEditForm.engagementType,
              environmentType: engEditForm.environmentType || null,
              // Not `|| null` like its neighbours. The UPDATE COALESCEs this
              // column, so null means "keep what is stored" -- the dropdown
              // would look like it worked, return 200, and change nothing.
              testingApproach: engEditForm.testingApproach || DEFAULT_APPROACH,
              ...(canAssignTester ? { testerId: engEditForm.testerId || null } : {}),
              startDate: engEditForm.startDate || null,
              endDate: engEditForm.endDate || null,
              crestRequired: engEditForm.crestRequired,
              pciRequired: engEditForm.pciRequired,
              thirdParty: engEditForm.thirdParty,
              vendorId: engEditForm.thirdParty ? (engEditForm.vendorId || editVendors.find(v => v.is_msp_default)?.id || null) : null,
              clientObligation: !!engEditForm.clientObligation,
              clientObligationFor: engEditForm.clientObligation ? (engEditForm.clientObligationFor || null) : null,
              customerTrackingId: engEditForm.customerTrackingId || null,
              serviceOrder: engEditForm.serviceOrder || null,
              jiraProjectKey: engEditForm.jiraProjectKey || null,
              billingAmount: isAdmin ? (engEditForm.billingAmount || 0) : undefined,
              notes: engEditForm.notes || null,
              ...(isAppType ? {
                companyOwned: engEditForm.companyOwned === 'true',
                companyHosted: engEditForm.companyHosted === 'true',
                internetFacing: engEditForm.internetFacing === 'true',
                paymentFunction: engEditForm.paymentFunction === 'true',
                fileUploadFunction: engEditForm.fileUploadFunction === 'true',
                ipWhitelistNeeded: engEditForm.ipWhitelistNeeded === 'true',
                scopingComments: engEditForm.scopingComments || null,
              } : {}),
              testingRestrictions: engEditForm.testingRestrictions?.noRestriction ? null : engEditForm.testingRestrictions,
              sowId: engEditForm.sowId || null,
              sowCredits: parseFloat(engEditForm.sowCredits) || 1,
              singlePointOfContact: engEditForm.singlePointOfContact || null,
              technicalManager: engEditForm.technicalManager || null,
              followUpDate: engEditForm.followUpDate || null,
              challengesDetails: engEditForm.challengesDetails || null,
            });
            setShowEditEngModal(false);
          }}>Save Changes</Button>
        </div>
      </Modal>

      {/* Scope Modal */}
      {/* Scope from the Networks inventory. Two ways in: tick entries the
          client already has on record, or add a new one here — which saves it
          to the inventory and puts it in scope in the same step, so there is
          no reason to leave and come back. */}
      <Modal isOpen={showNetworkPicker} onClose={() => setShowNetworkPicker(false)}
        title="Add scope from Networks" width={640}>
        {networkError && (
          <div style={{ padding: '8px 10px', marginBottom: 12, borderRadius: 6, fontSize: 12,
                        background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', color: '#dc2626' }}>
            {networkError}
          </div>
        )}

        {(() => {
          const inScope = new Set((eng.scope_items || [])
            .map(s => String(s.value || '').trim().toLowerCase()));
          const available = networkOptions.filter(n => !inScope.has(String(n.value || '').trim().toLowerCase()));
          const already = networkOptions.length - available.length;

          // Tags offered for filtering, commonest first, counted across what
          // is still addable — a tag every remaining entry shares is no use as
          // a filter, and one on nothing addable should not be offered at all.
          const tagCounts = new Map();
          for (const n of available) {
            for (const t of (n.tags || [])) tagCounts.set(t, (tagCounts.get(t) || 0) + 1);
          }
          const tagOptions = [...tagCounts.entries()]
            .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));

          const q = pickerFilter.search.trim().toLowerCase();
          const shown = available.filter(n => {
            if (pickerFilter.pciOnly && !n.pci) return false;
            if (pickerFilter.tag && !(n.tags || []).includes(pickerFilter.tag)) return false;
            if (!q) return true;
            // One box over everything a person might recognise an entry by,
            // including the tags — searching for "pci" should find the rows
            // tagged pci as readily as the ones named for it.
            const hay = [n.name, n.value, n.purpose, n.location, n.cloud_environment,
                         ...(n.tags || [])].filter(Boolean).join(' ').toLowerCase();
            return hay.includes(q);
          });
          const hiddenByFilter = available.length - shown.length;
          // Selections survive filtering, so a tick made under one filter is
          // not quietly dropped by the next. Any that are currently hidden are
          // called out beside the button rather than added invisibly.
          const selectedHidden = selectedNetworkIds.filter(
            id => !shown.some(n => n.id === id)).length;

          return (
            <>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                This client's networks
              </div>

              {available.length > 0 && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
                  <input value={pickerFilter.search}
                    placeholder="Search name, IP, purpose, tag…"
                    onChange={e => setPickerFilter(f => ({ ...f, search: e.target.value }))}
                    style={{ flex: 1, minWidth: 200, padding: '6px 10px', borderRadius: 6, fontSize: 12,
                             fontFamily: 'inherit', background: 'var(--bg-input)', color: 'var(--text)',
                             border: '1px solid var(--border)' }} />
                  {tagOptions.length > 0 && (
                    <select value={pickerFilter.tag}
                      onChange={e => setPickerFilter(f => ({ ...f, tag: e.target.value }))}
                      style={{ padding: '6px 10px', borderRadius: 6, fontSize: 12, fontFamily: 'inherit',
                               background: 'var(--bg-input)', border: '1px solid var(--border)',
                               color: pickerFilter.tag ? 'var(--primary)' : 'var(--text-secondary)', cursor: 'pointer' }}>
                      <option value="">All tags</option>
                      {tagOptions.map(([t, n]) => <option key={t} value={t}>{t} ({n})</option>)}
                    </select>
                  )}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, cursor: 'pointer',
                                  padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)',
                                  color: 'var(--text-secondary)',
                                  background: pickerFilter.pciOnly ? 'rgba(239,68,68,0.08)' : 'var(--bg-input)' }}>
                    <input type="checkbox" checked={pickerFilter.pciOnly}
                      onChange={e => setPickerFilter(f => ({ ...f, pciOnly: e.target.checked }))} />
                    PCI only
                  </label>
                  {(q || pickerFilter.tag || pickerFilter.pciOnly) && (
                    <button type="button"
                      onClick={() => setPickerFilter({ search: '', tag: '', pciOnly: false })}
                      style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer',
                               fontSize: 12, fontFamily: 'inherit' }}>Clear</button>
                  )}
                </div>
              )}
              {networkOptions.length === 0 ? (
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', padding: '10px 0' }}>
                  Nothing on record for this client yet. Add one below, or build the
                  inventory on the Networks page.
                </div>
              ) : (
                <>
                  <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
                    {shown.map(n => (
                      <label key={n.id} style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                        borderBottom: '1px solid var(--border)', cursor: 'pointer', fontSize: 13,
                      }}>
                        <input type="checkbox" checked={selectedNetworkIds.includes(n.id)}
                          onChange={e => setSelectedNetworkIds(ids =>
                            e.target.checked ? [...ids, n.id] : ids.filter(x => x !== n.id))} />
                        <span style={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace', minWidth: 150 }}>{n.value}</span>
                        <span style={{ fontWeight: 600 }}>{n.name}</span>
                        {n.pci && <Badge style={{ background: 'rgba(239,68,68,0.12)', color: '#dc2626' }}>PCI</Badge>}
                        {(n.tags || []).map(t => (
                          <span key={t} style={{ padding: '1px 7px', borderRadius: 10, fontSize: 11,
                                                 background: 'rgba(79,143,255,0.10)', color: '#4f8fff' }}>{t}</span>
                        ))}
                        <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                          {[n.purpose, n.location, n.cloud_environment].filter(Boolean).join(' · ')}
                        </span>
                      </label>
                    ))}
                    {shown.length === 0 && (
                      <div style={{ padding: '10px 12px', fontSize: 13, color: 'var(--text-secondary)' }}>
                        {/* "Nothing matches" and "nothing left to add" are
                            different situations and a single message for both
                            sends someone looking for entries that are there. */}
                        {available.length === 0
                          ? "Every entry on record is already in this engagement's scope."
                          : 'No entries match this filter.'}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
                    <Button onClick={addSelectedNetworks} disabled={!selectedNetworkIds.length || networkBusy}>
                      {networkBusy ? 'Adding…' : `Add ${selectedNetworkIds.length || ''} to scope`.trim()}
                    </Button>
                    {shown.length > 0 && (() => {
                      // Select-all acts on what is in front of you, not on the
                      // whole inventory — ticking forty hidden rows from a
                      // filtered view is never what anyone means by it.
                      const allShownSelected = shown.every(n => selectedNetworkIds.includes(n.id));
                      return (
                        <button type="button" onClick={() => setSelectedNetworkIds(ids => allShownSelected
                          ? ids.filter(id => !shown.some(n => n.id === id))
                          : [...new Set([...ids, ...shown.map(n => n.id)])])}
                          style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}>
                          {allShownSelected ? 'Clear these' : `Select these ${shown.length}`}
                        </button>
                      );
                    })()}
                    {selectedHidden > 0 && (
                      <span style={{ fontSize: 11, color: '#f59e0b' }}>
                        {selectedHidden} selected but hidden by the filter — they will be added too
                      </span>
                    )}
                    {hiddenByFilter > 0 && selectedHidden === 0 && (
                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                        {hiddenByFilter} hidden by the filter
                      </span>
                    )}
                    {already > 0 && (
                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                        {already} already in scope, not shown
                      </span>
                    )}
                  </div>
                </>
              )}

              <div style={{ borderTop: '1px solid var(--border)', marginTop: 16, paddingTop: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                  Or add a new one
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <Input label="Name" value={newNetwork.name} placeholder="e.g. DMZ"
                      onChange={e => setNewNetwork(f => ({ ...f, name: e.target.value }))} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <Input label="IP Address or CIDR Range" value={newNetwork.value} placeholder="10.0.0.0/24"
                      onChange={e => setNewNetwork(f => ({ ...f, value: e.target.value }))} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <Input label="Purpose" value={newNetwork.purpose}
                      onChange={e => setNewNetwork(f => ({ ...f, purpose: e.target.value }))} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <Select label="In PCI Scope" value={newNetwork.pci}
                      options={[{ value: 'false', label: 'No' }, { value: 'true', label: 'Yes' }]}
                      onChange={e => setNewNetwork(f => ({ ...f, pci: e.target.value }))} />
                  </div>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10 }}>
                  Saved to this client's Networks inventory and added to scope here.
                </div>
                <Button variant="secondary" onClick={createAndAddNetwork}
                  disabled={networkBusy || !newNetwork.name.trim() || !newNetwork.value.trim()}>
                  {networkBusy ? 'Saving…' : 'Create and add to scope'}
                </Button>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                <Button variant="secondary" onClick={() => setShowNetworkPicker(false)}>Close</Button>
              </div>
            </>
          );
        })()}
      </Modal>

      <Modal isOpen={showScopeModal} onClose={() => setShowScopeModal(false)} title="Add Scope Item">
        <Select label="Type *" options={[{ value: '', label: 'Select type' }, ...SCOPE_TYPES.map(s => ({ value: s, label: s }))]}
          value={scopeForm.scopeType || ''} onChange={e => setScopeForm(f => ({ ...f, scopeType: e.target.value }))} />
        <Input label="Value *" placeholder="e.g., https://example.com or 192.168.1.0/24"
          value={scopeForm.value || ''} onChange={e => setScopeForm(f => ({ ...f, value: e.target.value }))} />
        <Input label="Description" value={scopeForm.description || ''} onChange={e => setScopeForm(f => ({ ...f, description: e.target.value }))} />
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={() => setShowScopeModal(false)}>Cancel</Button>
          <Button onClick={addScope} disabled={!scopeForm.scopeType || !scopeForm.value}>Add</Button>
        </div>
      </Modal>

      {/* Finding Modal */}
      {/* Read-only by default. A findings table is read far more often than it
          is edited, and opening a row straight into a form invites changes
          nobody meant to make. Editing is one deliberate click further. */}
      <Modal isOpen={!!viewFinding} onClose={() => { setViewFinding(null); setEvidence([]); }}
        title={viewFinding?.title || 'Finding'} width={860}>
        {viewFinding && (() => {
          const f = viewFinding;
          let steps = [];
          try {
            const parsed = typeof f.steps_to_reproduce === 'string'
              ? JSON.parse(f.steps_to_reproduce) : f.steps_to_reproduce;
            if (Array.isArray(parsed)) steps = parsed;
          } catch { /* plain text, rendered below */ }
          const evidenceUrl = (findingId, evId) =>
            `/api/findings/${id}/${findingId}/evidence/${evId}/file?token=${sessionStorage.getItem('token')}`;
          const Row = ({ label, children }) => (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', marginBottom: 5 }}>{label}</div>
              <div style={{ fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{children}</div>
            </div>
          );
          return (
            <>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 16 }}>
                <Badge severity={f.severity}>{f.severity}</Badge>
                <Badge>{f.status || 'Open'}</Badge>
                {f.finding_uid && (
                  <span style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--text-secondary)' }}>{f.finding_uid}</span>
                )}
                {f.cvss_score != null && f.cvss_score !== '' && (
                  <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>CVSS {f.cvss_score}</span>
                )}
                <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                  Directly exploitable: {f.direct_exploitability || 'No'}
                </span>
              </div>

              {f.details && <Row label="Details of vulnerability">{f.details}</Row>}
              {f.affected_endpoints && <Row label="Affected endpoints">{f.affected_endpoints}</Row>}
              {f.observation && <Row label="Observation">{f.observation}</Row>}

              <Row label="Steps to reproduce">
                {steps.length > 0 ? (
                  <ol style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {steps.map((st, i) => (
                      <li key={i} style={{ lineHeight: 1.6 }}>
                        {typeof st === 'string' ? st : st.description}
                        {/* Evidence is tied to a step by index, the same way the
                            report places it. */}
                        {evidence.filter(e => e.step_index === i).map(ev => (
                          <div key={ev.id} style={{ marginTop: 8 }}>
                            <img src={evidenceUrl(f.id, ev.id)} alt={ev.caption || 'Evidence'}
                              style={{ maxWidth: '100%', borderRadius: 6, border: '1px solid var(--border)' }} />
                            {ev.caption && (
                              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{ev.caption}</div>
                            )}
                          </div>
                        ))}
                      </li>
                    ))}
                  </ol>
                ) : (f.steps_to_reproduce || <span style={{ color: 'var(--text-muted)' }}>Not recorded.</span>)}
              </Row>

              {f.recommendation && <Row label="Recommendation">{f.recommendation}</Row>}
              {f.references && <Row label="References">{f.references}</Row>}

              {evidence.filter(e => e.step_index == null).length > 0 && (
                <Row label="General evidence">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {evidence.filter(e => e.step_index == null).map(ev => {
                      const isImage = /\.(png|jpe?g|gif|webp)$/i.test(ev.file_name || '');
                      return (
                        <div key={ev.id}>
                          {isImage ? (
                            <img src={evidenceUrl(f.id, ev.id)} alt={ev.caption || 'Evidence'}
                              style={{ maxWidth: '100%', borderRadius: 6, border: '1px solid var(--border)' }} />
                          ) : (
                            <a href={evidenceUrl(f.id, ev.id)} target="_blank" rel="noopener noreferrer"
                              style={{ fontSize: 12, color: 'var(--primary)' }}>{ev.file_name}</a>
                          )}
                          {ev.caption && (
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{ev.caption}</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </Row>
              )}

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
                <Button variant="secondary" onClick={() => { setViewFinding(null); setEvidence([]); }}>Close</Button>
                {canEdit && (
                  <Button onClick={() => {
                    setEditFinding(f); setFindingForm(findingFormFrom(f)); loadEvidence(f.id);
                    setViewFinding(null); setShowFindingModal(true);
                  }}>Edit</Button>
                )}
              </div>
            </>
          );
        })()}
      </Modal>

      <Modal isOpen={showFindingModal} onClose={() => { setShowFindingModal(false); setEditFinding(null); setEvidence([]); setEditingCaption(null); }} title={editFinding ? 'Edit Finding' : 'Add Finding'} width={800} noOverlayClose>
        {/* Only when adding. On an edit it would silently overwrite work
            already done on a real finding, and there is no undo in this form. */}
        {!editFinding && (
          <TemplatePicker templates={vulnTemplates} onPick={applyTemplate} />
        )}
        <Input label="Title *" value={findingForm.title || ''} onChange={e => setFindingForm(f => ({ ...f, title: e.target.value }))} />
        <CvssCalculator
          value={{ vector: findingForm.cvssTags ?? '' }}
          onChange={({ score, vector, severity }) => {
            setFindingForm(f => ({
              ...f,
              ...(score !== null ? { cvssScore: score } : {}),
              cvssTags: vector,
              ...(severity ? { severity } : {}),
            }));
          }}
        />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <Select label="Severity *" options={[{ value: '', label: 'Select' }, ...SEVERITIES.map(s => ({ value: s, label: s }))]}
            value={findingForm.severity || ''} onChange={e => setFindingForm(f => ({ ...f, severity: e.target.value }))} />
          <Input label="CVSS Score" type="number" min="0" max="10" step="0.1"
            value={findingForm.cvssScore ?? ''} onChange={e => setFindingForm(f => ({ ...f, cvssScore: e.target.value }))} />
          <Select label="Status" options={[{ value: 'Open', label: 'Open' }, { value: 'Closed', label: 'Closed' }, { value: 'Accepted', label: 'Accepted' }, { value: 'Retest', label: 'Retest' }, { value: 'Reopened', label: 'Reopened' }]}
            value={findingForm.status || 'Open'} onChange={e => setFindingForm(f => ({ ...f, status: e.target.value }))} />
        </div>
        <Input label="CVSS Vector" value={findingForm.cvssTags ?? ''} onChange={e => setFindingForm(f => ({ ...f, cvssTags: e.target.value }))} placeholder="e.g., CVSS:3.1/AV:N/AC:L/PR:N/UI:N/S:U/C:H/I:H/A:H" />
        {/* Optional on purpose, and never pre-selected to a code. Automation
            writes most findings here, and forcing a tester to choose on the
            handful they type by hand buys a guess rather than a fact. */}
        <Select label="OWASP Top 10:2021" options={OWASP_OPTIONS}
          value={findingForm.owaspCategory ?? ''}
          onChange={e => setFindingForm(f => ({ ...f, owaspCategory: e.target.value }))} />
        {/* Both optional, and both printed by the TSR finding card. Free
            text for CWE because the list is MITRE's, versioned and 900
            entries long; a fixed list for effort because that vocabulary is
            ours and the report defines it. */}
        <Input label="CWE" value={findingForm.cwe ?? ''}
          onChange={e => setFindingForm(f => ({ ...f, cwe: e.target.value }))}
          placeholder="e.g., CWE-434 Unrestricted Upload of File with Dangerous Type" />
        <Select label="Remediation Effort"
          options={[{ value: '', label: 'Not Set' }, { value: 'Low', label: 'Low' },
                    { value: 'Medium', label: 'Medium' }, { value: 'High', label: 'High' }]}
          value={findingForm.remediationEffort ?? ''}
          onChange={e => setFindingForm(f => ({ ...f, remediationEffort: e.target.value }))} />
        <Select label="Directly Exploitable" value={findingForm.directExploitability ?? 'No'}
          onChange={e => setFindingForm(f => ({ ...f, directExploitability: e.target.value }))}
          options={[{ value: 'No', label: 'No' }, { value: 'Yes', label: 'Yes' }]} />
        <TextArea label="Vulnerability Details" value={findingForm.details || ''} onChange={e => setFindingForm(f => ({ ...f, details: e.target.value }))} />
        <TextArea label="Affected Endpoints" value={findingForm.affectedEndpoints ?? ''} onChange={e => setFindingForm(f => ({ ...f, affectedEndpoints: e.target.value }))} />
        <TextArea label="Observation" value={findingForm.observation || ''} onChange={e => setFindingForm(f => ({ ...f, observation: e.target.value }))} />
        <StepsToReproduce
          key={templateFillKey}
          value={findingForm.stepsToReproduce ?? ''}
          onChange={v => setFindingForm(f => ({ ...f, stepsToReproduce: v }))}
          evidence={evidence}
          engagementId={id}
          findingId={editFinding?.id}
          apiFetch={apiFetch}
          onEvidenceChange={() => loadEvidence(editFinding?.id)}
        />

        {/* General Evidence (not linked to a step) */}
        {editFinding && evidence.filter(e => e.step_index == null).length > 0 && (
          <div style={{ margin: '16px 0', padding: 16, background: 'var(--bg-input)', borderRadius: 10, border: '1px solid var(--border)' }}>
            <h4 style={{ fontSize: 14, fontWeight: 600, margin: '0 0 12px' }}>General Evidence ({evidence.filter(e => e.step_index == null).length})</h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {evidence.filter(e => e.step_index == null).map(ev => (
                <div key={ev.id} style={{ position: 'relative', borderRadius: 6, overflow: 'hidden', border: '1px solid var(--border)' }}>
                  <img
                    src={`/api/findings/${id}/${editFinding.id}/evidence/${ev.id}/file?token=${sessionStorage.getItem('token')}`}
                    alt={ev.caption || ev.file_name}
                    style={{ maxHeight: 150, maxWidth: 250, display: 'block', cursor: 'pointer' }}
                    onClick={() => window.open(`/api/findings/${id}/${editFinding.id}/evidence/${ev.id}/file?token=${sessionStorage.getItem('token')}`, '_blank')}
                  />
                  <div style={{ padding: '4px 8px', fontSize: 11, color: 'var(--text-secondary)', background: 'var(--bg-card)' }}>
                    {ev.caption || ev.file_name}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <TextArea label="Recommendation" value={findingForm.recommendation || ''} onChange={e => setFindingForm(f => ({ ...f, recommendation: e.target.value }))} />
        <TextArea label="References" value={findingForm.references || ''} onChange={e => setFindingForm(f => ({ ...f, references: e.target.value }))} />

        {/* AI Suggest */}
        {canEdit && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <Button variant="secondary" style={{ fontSize: 12, padding: '6px 14px' }}
              onClick={requestAiSuggest} disabled={aiLoading || !findingForm.title || findingForm.title.length < 5}>
              {aiLoading ? 'Analyzing...' : '🤖 AI Suggest'}
            </Button>
          </div>
        )}
        <AiSuggestPanel
          suggestions={aiSuggestions}
          loading={aiLoading}
          error={aiError}
          onApply={applyAiSuggestion}
          // This panel sits inside the finding form, so a suggested template
          // fills it in like any other -- it used to create a separate finished
          // finding behind the form the tester was already filling in.
          onUseTemplate={(templateId) => {
            const t = (vulnTemplates || []).find(x => String(x.id) === String(templateId));
            if (t) { applyTemplate(t); setAiSuggestions(null); }
          }}
          onClose={() => { setAiSuggestions(null); setAiError(null); }}
        />

        {/* Remediation Timeline */}
        {editFinding && (
          <div style={{ borderTop: '1px solid var(--border)', marginTop: 16, paddingTop: 16 }}>
            {/* The id, not the row. editFinding is the whole finding object
                everywhere else in this file, and handing it over interpolated
                as [object Object], so the timeline fetch 500'd and the panel
                read "No remediation activity yet." on every finding -- the one
                state an empty timeline and a broken one share. */}
            <RemediationTimeline findingId={editFinding.id} />

            {/* Mark Remediated button for client users on Open/Reopened findings */}
            {user?.clientId && ['Open', 'Reopened'].includes(findingForm.status) && !showRemediateForm && (
              <Button style={{ marginTop: 8, background: '#3b82f6' }} onClick={() => setShowRemediateForm(true)}>
                Mark as Remediated
              </Button>
            )}
            {showRemediateForm && (
              <div style={{ marginTop: 8, padding: 12, background: 'var(--bg-input)', borderRadius: 8, border: '1px solid var(--border)' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>Remediation Notes *</div>
                <textarea value={remediateNotes} onChange={e => setRemediateNotes(e.target.value)} rows={3}
                  placeholder="Describe what was fixed and how..."
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: 13, background: 'var(--bg)', color: 'var(--text)', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', marginBottom: 8 }} />
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button onClick={() => submitRemediation(editFinding.id)}>Submit for Retest</Button>
                  <Button variant="secondary" onClick={() => { setShowRemediateForm(false); setRemediateNotes(''); }}>Cancel</Button>
                </div>
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
          <Button variant="secondary" onClick={() => { setShowFindingModal(false); setEditFinding(null); setEvidence([]); setEditingCaption(null); setShowRemediateForm(false); setRemediateNotes(''); }}>Cancel</Button>
          <Button onClick={saveFinding} disabled={!findingForm.title || !findingForm.severity}>Save Finding</Button>
        </div>
      </Modal>

      <Modal isOpen={showBurpModal} onClose={() => setShowBurpModal(false)} title="Import Findings from Burp Suite" width={620}>
        <div style={{ marginBottom: 20, padding: 16, background: 'var(--bg-input)', borderRadius: 8, fontSize: 13, lineHeight: 1.7 }}>
          <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 14 }}>How to Export from Burp Suite:</div>
          <ol style={{ margin: 0, paddingLeft: 20 }}>
            <li>In Burp Suite, go to the <strong>Target</strong> tab or <strong>Dashboard</strong></li>
            <li>Select the issues you want to export (or select all)</li>
            <li>Right-click and choose <strong>"Report selected issues"</strong></li>
            <li>Select <strong>XML</strong> as the report format</li>
            <li>Save the file and upload it below</li>
          </ol>
          <div style={{ marginTop: 12, color: 'var(--text-secondary)', fontSize: 12 }}>
            Duplicate issues (same vulnerability on the same host) will be automatically merged, combining their affected endpoints into a single finding.
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: 'var(--text-secondary)' }}>Burp XML File *</label>
          <input type="file" accept=".xml" onChange={e => setBurpFile(e.target.files[0])}
            style={{ fontSize: 14, color: 'var(--text)' }} />
        </div>

        {burpResult && (
          <div style={{
            padding: 12, borderRadius: 8, marginBottom: 16, fontSize: 13,
            background: burpResult.error ? '#7f1d1d' : '#14532d',
            color: burpResult.error ? '#fca5a5' : '#86efac'
          }}>
            {burpResult.error ? (
              <div>{burpResult.error}</div>
            ) : (
              <div>
                <div>Successfully imported <strong>{burpResult.imported}</strong> finding{burpResult.imported !== 1 ? 's' : ''} from {burpResult.totalInFile} issue{burpResult.totalInFile !== 1 ? 's' : ''}.</div>
                {burpResult.deduplicated > 0 && (
                  <div style={{ marginTop: 4 }}>{burpResult.deduplicated} duplicate{burpResult.deduplicated !== 1 ? 's' : ''} merged.</div>
                )}
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={() => setShowBurpModal(false)}>Close</Button>
          <Button style={{ background: '#f97316', color: '#fff' }}
            onClick={importBurpXml} disabled={!burpFile || burpImporting}>
            {burpImporting ? 'Importing...' : 'Import'}
          </Button>
        </div>
      </Modal>

      {/* Report Template Selection Modal */}
      <Modal isOpen={showReportModal} onClose={() => !generating && setShowReportModal(false)} title="Generate Report" width={500}>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--text)' }}>
            Select Template
          </label>
          {reportTemplates.length === 0 ? (
            <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>
              No templates available. Create a template first.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {reportTemplates.map(t => (
                <button key={t.id} onClick={() => setSelectedTemplateId(t.id)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 14px', borderRadius: 'var(--radius)',
                    border: selectedTemplateId === t.id ? '2px solid var(--primary)' : '1px solid var(--border)',
                    background: selectedTemplateId === t.id ? 'var(--bg-input)' : 'var(--bg-card)',
                    cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s',
                    color: 'var(--text)'
                  }}
                >
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{t.name}</div>
                    {t.description && (
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{t.description}</div>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    {t.is_default && (
                      <span style={{
                        fontSize: 10, fontWeight: 600, padding: '2px 8px',
                        background: 'var(--primary)', color: '#fff', borderRadius: 10
                      }}>DEFAULT</span>
                    )}
                    {selectedTemplateId === t.id && (
                      <span style={{ fontSize: 16, color: 'var(--primary)' }}>✓</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        {/* Export format — hidden when the backend offers only one format. */}
        {reportFormats.length > 1 && (
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--text)' }}>
            Export Format
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            {[
              { value: 'docx', label: 'Word (.docx)', icon: '\ud83d\udcdd' },
              { value: 'pdf', label: 'PDF', icon: '\ud83d\udcc4' },
            ].filter(fmt => reportFormats.includes(fmt.value)).map(fmt => {
              const on = selectedFormats.includes(fmt.value);
              return (
                <button key={fmt.value} type="button" aria-pressed={on}
                  onClick={() => setSelectedFormats(prev => (
                    prev.includes(fmt.value) ? prev.filter(v => v !== fmt.value) : [...prev, fmt.value]
                  ))}
                  style={{
                    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    padding: '10px 16px', borderRadius: 'var(--radius)',
                    border: on ? '2px solid var(--primary)' : '1px solid var(--border)',
                    background: on ? 'var(--bg-input)' : 'var(--bg-card)',
                    cursor: 'pointer', fontSize: 14, fontWeight: 600,
                    color: on ? 'var(--primary)' : 'var(--text)',
                    transition: 'all 0.15s'
                  }}
                >
                  <span style={{ fontSize: 18 }}>{fmt.icon}</span>
                  <span>{fmt.label}</span>
                  <span style={{ fontSize: 13, width: 12, color: 'var(--primary)' }}>{on ? '\u2713' : ''}</span>
                </button>
              );
            })}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
            {selectedFormats.length === 0
              ? 'Choose at least one format.'
              : selectedFormats.length > 1
                ? 'Both files are produced from the same render, as one version.'
                : 'Tick both to get a Word file and a PDF.'}
          </div>
        </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--text)' }}>
            Report Name
          </label>
          <input type="text" value={reportName}
            onChange={e => setReportName(e.target.value)}
            placeholder={suggestedReportName || 'Report name'}
            style={{
              width: '100%', padding: '8px 12px', borderRadius: 'var(--radius)',
              border: '1px solid var(--border)', background: 'var(--bg-input)',
              color: 'var(--text)', fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box',
            }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              The {selectedFormats.length > 1
                ? '.docx and .pdf extensions are'
                : `${selectedFormats[0] === 'pdf' ? '.pdf' : '.docx'} extension is`} added automatically.
              Letters, numbers, spaces, hyphens and underscores only.
            </span>
            {suggestedReportName && reportName !== suggestedReportName && (
              <button type="button" onClick={() => setReportName(suggestedReportName)}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                         fontSize: 12, color: 'var(--primary)', fontFamily: 'inherit' }}>
                Reset to default
              </button>
            )}
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--text)' }}>
            Version
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="number" step="0.1" min="0.1" value={reportVersion}
              onChange={e => setReportVersion(e.target.value)}
              style={{
                width: 100, padding: '8px 12px', borderRadius: 'var(--radius)',
                border: '1px solid var(--border)', background: 'var(--bg-input)',
                color: 'var(--text)', fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
              }} />
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Use minor versions (e.g. 1.1, 1.2) for revisions
            </span>
          </div>
        </div>

        {(user?.isMsp || hasGroup('Super Admins')) && (
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 16, cursor: 'pointer' }}>
            <input type="checkbox" checked={includeCertificate} style={{ marginTop: 3 }}
              onChange={e => setIncludeCertificate(e.target.checked)} />
            <span>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>Certificate of authenticity</span>
              <span style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)' }}>
                Adds a verification page and QR code, and records a certificate the client can
                check. Leave unticked for a draft.
              </span>
            </span>
          </label>
        )}

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={() => setShowReportModal(false)} disabled={generating}>Cancel</Button>
          <Button variant="success" onClick={() => generateReport(selectedTemplateId, selectedFormats)}
            disabled={!selectedTemplateId || generating || selectedFormats.length === 0}>
            {generating ? 'Generating...' : (() => {
              const wantsDocx = selectedFormats.includes('docx');
              const wantsPdf = selectedFormats.includes('pdf');
              if (wantsDocx && wantsPdf) return '\ud83d\udcdd Generate Word + PDF';
              if (wantsPdf) return '\ud83d\udcc4 Generate PDF';
              if (wantsDocx) return '\ud83d\udcdd Generate Word';
              return 'Generate';
            })()}
          </Button>
        </div>
      </Modal>

      {/* Link Application Modal */}
      <Modal isOpen={showLinkAppModal} onClose={() => setShowLinkAppModal(false)}
        title="Link an application" width={520}>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 14 }}>
          Choose the application this engagement tested. Only this link changes &mdash; the
          engagement&rsquo;s own scoping details are left as they are.
        </p>
        {linkAppError && (
          <div style={{ padding: 10, borderRadius: 6, background: '#fef2f2', border: '1px solid #fecaca',
                        color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{linkAppError}</div>
        )}
        {linkableApps.length === 0 && !linkAppError ? (
          <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            This client has no applications yet. An Application Manager can add one first.
          </p>
        ) : (
          <Select label="Application" value={linkAppId}
            onChange={e => setLinkAppId(e.target.value)}
            options={[{ value: '', label: '— Select Application —' },
                      ...linkableApps.map(a => ({
                        value: a.id,
                        label: a.customer_tracking_id ? `${a.name} - ${a.customer_tracking_id}` : a.name,
                      }))]} />
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <Button variant="secondary" onClick={() => setShowLinkAppModal(false)}>Cancel</Button>
          <Button onClick={linkApplication} disabled={!linkAppId || linkingApp}>
            {linkingApp ? 'Linking...' : 'Link application'}
          </Button>
        </div>
      </Modal>

      {/* Credential Modal */}
      <Modal isOpen={showCredentialModal} onClose={() => { setShowCredentialModal(false); setEditingCredId(null); }}
        title={editingCredId ? 'Edit Credential' : 'Add Credential'} width={520}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {editingCredId && (
            <div style={{ padding: 10, borderRadius: 6, background: 'var(--bg-input)',
                          border: '1px solid var(--border)', fontSize: 13, color: 'var(--text-secondary)' }}>
              Stored secrets cannot be displayed. Leave the secret and username blank to keep the
              current ones, or type a replacement.
            </div>
          )}
          <Select label="Credential Type" value={newCredential.credential_type}
            onChange={e => setNewCredential({ ...newCredential, credential_type: e.target.value, username: '',
              // Keep the existing settings when editing: changing the type
              // should not silently discard a login URL that still applies.
              extra_config: editingCredId ? newCredential.extra_config : {} })}
            options={[
              { value: 'basic_auth', label: 'HTTP Basic Auth' },
              { value: 'bearer_token', label: 'Bearer Token' },
              { value: 'api_key', label: 'API Key' },
              { value: 'cookie', label: 'Cookie' },
              { value: 'form_login', label: 'Form Login' },
            ]}
          />
          <Input label="Label" placeholder="e.g. Admin Account, API Token" value={newCredential.label}
            onChange={e => setNewCredential({ ...newCredential, label: e.target.value })} />

          {(newCredential.credential_type === 'basic_auth' || newCredential.credential_type === 'form_login') && (
            <Input label={`Username${editingCredId ? ' (leave blank to keep)' : ''}`}
              value={newCredential.username}
              onChange={e => setNewCredential({ ...newCredential, username: e.target.value })} />
          )}

          <Input label={(
            newCredential.credential_type === 'basic_auth' ? 'Password' :
            newCredential.credential_type === 'bearer_token' ? 'Token' :
            newCredential.credential_type === 'api_key' ? 'Key Value' :
            newCredential.credential_type === 'cookie' ? 'Cookie String' : 'Password'
          ) + (editingCredId ? ' (leave blank to keep)' : '')} type="password" value={newCredential.secret}
            onChange={e => setNewCredential({ ...newCredential, secret: e.target.value })}
            placeholder={
              newCredential.credential_type === 'cookie' ? 'session=abc123; auth=xyz789' :
              newCredential.credential_type === 'bearer_token' ? 'eyJhbGciOiJIUzI1NiIs...' : ''
            }
          />

          {newCredential.credential_type === 'api_key' && (
            <Input label="Header Name" placeholder="X-API-Key" value={newCredential.extra_config.header_name || ''}
              onChange={e => setNewCredential({ ...newCredential, extra_config: { ...newCredential.extra_config, header_name: e.target.value } })} />
          )}

          {newCredential.credential_type === 'form_login' && (
            <>
              <Input label="Login URL" placeholder="https://target.com/login" value={newCredential.extra_config.login_url || ''}
                onChange={e => setNewCredential({ ...newCredential, extra_config: { ...newCredential.extra_config, login_url: e.target.value } })} />
              <div style={{ display: 'flex', gap: 12 }}>
                <Input label="Username Field" placeholder="username" style={{ flex: 1 }}
                  value={newCredential.extra_config.username_field || ''}
                  onChange={e => setNewCredential({ ...newCredential, extra_config: { ...newCredential.extra_config, username_field: e.target.value } })} />
                <Input label="Password Field" placeholder="password" style={{ flex: 1 }}
                  value={newCredential.extra_config.password_field || ''}
                  onChange={e => setNewCredential({ ...newCredential, extra_config: { ...newCredential.extra_config, password_field: e.target.value } })} />
              </div>
            </>
          )}

          <div style={{ padding: '10px 14px', background: 'var(--bg-input)', borderRadius: 8, fontSize: 13, color: 'var(--text-secondary)' }}>
            🔒 Credentials are encrypted at rest and only decrypted during scan execution.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 20 }}>
          <Button variant="secondary" onClick={() => setShowCredentialModal(false)}>Cancel</Button>
          {/* A secret is required to create one, but not to edit: blank keeps
              whatever is stored, which is the only way to edit a label without
              knowing the password. */}
          <Button onClick={saveCredential}
            disabled={!newCredential.label || (!editingCredId && !newCredential.secret)}>
            {editingCredId ? 'Save Changes' : 'Save Credential'}
          </Button>
        </div>
      </Modal>

      {/* AI Analysis Modal */}
      <Modal isOpen={showAiModal} onClose={() => { setShowAiModal(false); setAiResult(null); }}
        title={`AI Analysis: ${aiTitle}`} width={700}>
        {aiAnalyzing ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>🤖</div>
            <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--text-secondary)' }}>Analyzing finding with AI...</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 8 }}>This typically takes 5-15 seconds</div>
          </div>
        ) : aiResult?.error ? (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
            <div style={{ color: '#ef4444', fontWeight: 600, fontSize: 14 }}>{aiResult.error}</div>
          </div>
        ) : aiResult ? (
          <div style={{ maxHeight: 500, overflowY: 'auto' }}>
            <div style={{
              padding: 16, borderRadius: 8, background: 'var(--bg-input)',
              fontSize: 14, lineHeight: 1.7, whiteSpace: 'pre-wrap'
            }}>
              {aiResult.analysis || aiResult.content || 'No analysis content returned'}
            </div>
            <div style={{
              display: 'flex', gap: 16, marginTop: 12, padding: '8px 0',
              fontSize: 12, color: 'var(--text-secondary)', borderTop: '1px solid var(--border)'
            }}>
              {aiResult.model && <span>Model: {aiResult.model}</span>}
              {aiResult.tokens_used != null && <span>Tokens: {aiResult.tokens_used.toLocaleString()}</span>}
              {aiResult.cost_usd != null && <span>Cost: ${aiResult.cost_usd.toFixed(4)}</span>}
            </div>
          </div>
        ) : null}
      </Modal>

      {launchAgent && (
        <AgentScanLaunchModal engagement={eng} onClose={() => setLaunchAgent(false)} />
      )}
    </div>
  );
}
